import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Notice } from '@/components/ui/Notice';
import { SignerField } from '@/components/docusign/SignerField';
import { ApiError, api } from '@/lib/api';
import {
  CC_EXPLAINER,
  DUPLICATE_CC_EMAIL,
  DUPLICATE_SIGNER_EMAIL,
  EMPTY_SIGNER,
  MAX_CCS,
  MAX_SIGNERS,
  MULTIPLE_SIGNERS_ARE_EMAILED,
  SIGNER_CANNOT_BE_COPIED,
  SIGNER_NEEDS_EMAIL,
  ccErrorKey,
  docuSignEnvironmentLabel,
  signaturesNotBinding,
  signerErrorKey,
  type SignerValue,
} from '@/lib/docusign';
import {
  DEFAULT_SIGNATURE_MESSAGE,
  DEFAULT_SIGNATURE_SUBJECT,
  fillTemplate,
  resolveTemplate,
  signerFirstName,
} from '@/lib/docusign-templates';
import { documentCustomerName } from '@/lib/company-names';
import { formatDecimalString } from '@/lib/format';
import type {
  DocuSignConnection,
  DocuSignDocumentEnvelopeInput,
  DocuSignEmailTemplates,
  DocuSignEnvelope,
  Quote,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { linkOf } from '@/lib/xero';
import { PREVIEW_PDF_LABEL, canPreviewQuotePdf } from '@/lib/quote-pdf-preview';
import {
  QuotePdfPreviewModal,
  type QuotePdfPreviewTarget,
} from '@/components/quotes/QuotePdfPreview';
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Select,
  Skeleton,
  Textarea,
  type MultiComboboxOption,
} from '@/components/ui';

/*
 * Send a quote for signature.
 *
 * ── THE ONE SENTENCE THAT MATTERS ────────────────────────────────────────
 *
 * A COMPLETED SIGNATURE ACCEPTS THE QUOTE. It moves the quote to the accepted
 * stage with NO confirmation, because a signature is the route into that stage
 * with nobody at a screen when it completes. Acceptance does nothing else — no
 * invoice, no Xero push; those are separate things a person does afterwards.
 * So the only moment a person can be told is BEFORE they send. That is why the
 * consequence is a standing block in this form rather than a line in a
 * tooltip, and why it is restated in the confirmation the Send button opens.
 *
 * ── AND IT MAY BE WORTH NOTHING ──────────────────────────────────────────
 *
 * A sandbox envelope comes back marked "signed" and has no legal force:
 * somebody sends a customer a link, watches it complete, and has nothing.
 * **The failure mode looks exactly like success**, so this surface warns —
 * it is the last screen before the envelope leaves. The rule is
 * `signaturesNotBinding()` in `@/lib/docusign` and there is exactly ONE copy of
 * it: never write a second `=== 'demo'` test here or anywhere else.
 *
 * ⚠️ **THE WARNING NOW FIRES ONLY ON A READING, REVERSED 2026-08-31 on the
 * owner's instruction.** *SUPERSEDED: "The server credentials are the DocuSign
 * SANDBOX, deliberately … Note the connection read is ADMIN-ONLY while sending
 * takes any write role, so most people who open this dialog cannot read it at
 * all. That is not a reason to fall silent: `signaturesNotBinding(null)` is
 * `true`, so a 403, a 502 or a timeout warns rather than assuming production."*
 *
 * Both halves of that were falsified at once. **DocuSign is on production**, so
 * the warning was no longer merely cautious — it was WRONG; and because the
 * connection read is admin-only, it was wrong for **every non-admin sender, on
 * every quote**, which is most of the people who use this dialog. A red banner
 * that is false on every send is one nobody reads on the day it is true.
 *
 * So `connection` staying null now warns about nothing. The read is still made
 * here — a 403 is still swallowed silently — because an admin sender gets a
 * real answer from it, and because the backend is exposing the environment to
 * write roles: the day it does, this screen starts warning them with no change
 * here.
 *
 * ── UP TO FOUR SIGNERS, AND THE WIRE SHAPE CHANGED WITH THEM ─────────────
 *
 * Owner's decision, 2026-09-01: *"in some cases or in some companies there can be
 * multiple signers so we have to make a provision on the same template to add
 * multiple signers dynamically on the same form there should be a picker for the
 * number of signers near the signer and an add signer button."*
 *
 * Four rulings arrived with it and **none of them is open**:
 *
 *   1. **at most FOUR** — `MAX_SIGNERS`, enforced server-side too;
 *   2. **all signers receive it at once, in any order.** There is no per-signer
 *      ordering control and none is to be built;
 *   3. **more than one signer is EMAIL DELIVERY ONLY.** "I deliver the link
 *      myself" is unavailable above one, with the reason on screen — see
 *      `MULTIPLE_SIGNERS_ARE_EMAILED`;
 *   4. each signer is **independently** a contact at this quote's company or a
 *      hand-typed person, so every row is its own `SignerField`.
 *
 * ⚠️ **`signer` BECAME `signers`, AN ARRAY, AND THE ENDPOINT REFUSES BOTH AT
 * ONCE.** `POST /docusign/envelopes/documents` accepts either key and answers a
 * 422 if both arrive, so this form always sends the array — one signer included.
 * `FormValues.signers` is never empty and `buildDocumentPayload` builds no
 * `signer` key at all.
 *
 * ⚠️ **THE 422 KEYS ARE INDEXED, AND THE ERROR ROUTING HAD TO BE WIDENED FOR IT.**
 * A refusal arrives as `signers.0.email`. `knownWriteKeys` walked ONE LEVEL DEEP
 * and skipped arrays outright, so every such refusal would have landed in
 * `unroutable` — rendered as an unattached banner line, marked on no control, and
 * still counted in "one field needs attention, highlighted above". It descends
 * into arrays of objects now; the walk carries the whole story at its own site.
 *
 * ⚠️ **ONE SIGNER RENDERS EXACTLY AS IT DID.** No ordinal, no Remove control, no
 * row border, the same "Who signs" heading and the same "Signer" label — the
 * ordinary send is untouched, and that is deliberate rather than incidental.
 *
 * ── ONE SEND PATH. THE TEMPLATE PATH IS GONE ─────────────────────────────
 *
 * ⚠️ **REMOVED 2026-08-31, owner's instruction: "on the send quote, 'What gets
 * signed' will always be a quote PDF, so remove this section, no need to ask."**
 *
 * *SUPERSEDED, this section in full: "── TWO SEND PATHS, NOT TWO VARIANTS ──
 * `document` POST /docusign/envelopes/documents WE supply the PDF bytes;
 * `template` POST /docusign/envelopes DocuSign holds the document. They share
 * only `quoteId` and the signer. The backend keeps them as separate endpoints
 * with separate `.strict()` schemas for that reason, and this form keeps them as
 * separate payload builders — a union would report both shapes' validation
 * errors against whichever one the user chose. **Document is the real flow.**"*
 *
 * The reason the sentence gave for calling document "the real flow" is the same
 * reason the choice is gone: the quote PDF is produced by Xero with every figure
 * already rendered into it, and a template cannot express that — a fixed form
 * with a text field per line item does not survive a quote of a different
 * length.
 *
 * ⚠️ **WHAT THE TEMPLATE BRANCH CARRIED, so nobody has to reconstruct it.** It
 * built a `DocuSignTemplateEnvelopeInput` for `POST /docusign/envelopes` with
 * `templateId`, and `signer.roleName` alongside the signer's name and email.
 * `roleName` was a `Select` over `template.roles` and never typed, because a
 * role DocuSign cannot match produces a recipient-less envelope and a `200`.
 *
 * **NOTHING OF IT IS OWED TO THE SURVIVING PATH.** The two shared only
 * `quoteId`, `signer.{name,email}`, `delivery`, `emailSubject` and `emailBlurb`,
 * and every one of those is still built by `buildDocumentPayload`. There is no
 * half-built request here: `templateId` and `roleName` are keys the documents
 * endpoint never declared and — both schemas being `.strict()` — would have been
 * a 422 if they had been left in.
 *
 * `POST /docusign/envelopes` still exists server-side and `api` still exposes
 * `sendDocusignTemplateEnvelope`. This form simply no longer reaches it.
 *
 * ── WHERE THE PDF COMES FROM: THE CHOICE IS GONE, THE FALLBACK IS NOT ─────
 *
 * ⚠️ **THE TOGGLE WAS REMOVED 2026-08-31, owner's instruction: "'Where the PDF
 * comes from' — it will always come from Xero, so remove this section which
 * gives these two options."** *SUPERSEDED: "**The file picker stays a
 * first-class fallback, never a hidden one.** … So it is offered on every quote,
 * and it is the ONLY option on a quote that is not in Xero."*
 *
 * `documentBase64` and `documentName` are OPTIONAL, on the SAME path with the
 * same guards. Sending NEITHER makes the server fetch this quote's PDF from
 * Xero and name it from the Xero quote number, and the bytes never travel
 * through a browser.
 *
 *   fetch    what every send does when the quote is in Xero. Send neither key.
 *   upload   the sender's own file. Send BOTH keys, always together.
 *
 * 🔴 **UPLOAD IS NOT DELETED, AND DELETING IT WOULD STRAND TWO REFUSALS.** What
 * went is the CHOICE — there are no buttons, and nobody picks. The source is now
 * decided entirely by the form:
 *
 *   in Xero              -> `xero`. The only thing a person can do.
 *   NOT in Xero          -> `upload`. There is no document to fetch.
 *   422 QUOTE_NOT_IN_XERO -> flipped to `upload` by `send()`, as before.
 *
 * The last two are the whole reason the branch survives. `DOCUSIGN_QUOTE_NOT_IN
 * _XERO` is the ONE refusal recoverable inside the same request, and its only
 * recovery is the file picker; removing it would leave a 422 whose advice is
 * "use the control that no longer exists".
 *
 * ⚠️ **The ANCHOR recovery is INDEPENDENT of all this and is untouched.**
 * `ANCHOR_TAB_STRING_NOT_FOUND` reveals `anchorOverride`, not the file picker —
 * the two were never wired together, and neither removal above reaches it.
 *
 * ⚠️ **Bytes and a name travel TOGETHER.** `documentBase64` with no
 * `documentName` is a **422 on `documentName`** — absent-and-absent means
 * fetch, whereas bytes with no name means uploading something without saying
 * what it is, and defaulting that puts a meaningless filename in front of a
 * signer on a contract. `DocuSignDocumentEnvelopeInput` expresses that as a
 * union, so this form cannot build the refused shape at all.
 *
 * ── THE FETCH REFUSALS GO TO DIFFERENT PLACES ────────────────────────────
 *
 * ⚠️ *SUPERSEDED 2026-08-31: this heading read "THE THREE REFUSALS GO TO THREE
 * DIFFERENT PLACES" over a list of TWO.* It is titled by what the list is FOR
 * now, because a count in prose beside a list is the first thing to rot — and
 * this one was already wrong before a third code arrived to make it right.
 *
 *   422 DOCUSIGN_QUOTE_NOT_IN_XERO     never pushed. The ONLY one recoverable
 *                                      inside this same request — so it turns
 *                                      the file picker on and says so, rather
 *                                      than a "push to Xero first" dead end.
 *   409 DOCUSIGN_QUOTE_GONE_FROM_XERO  the link is archived, or Xero 404s the
 *                                      document. Discovered two ways, one
 *                                      state: the Xero document is gone, and
 *                                      nothing on this screen conjures it back.
 *   409 DOCUSIGN_QUOTE_STILL_DRAFT_IN_XERO
 *                                      the quote is in Xero and could not be
 *                                      moved OUT of DRAFT, so its PDF would
 *                                      carry Xero's DRAFT stamp. Refused rather
 *                                      than sent. Nothing here fixes it — not
 *                                      the file picker, not the anchor — so it
 *                                      states the server's sentence and stops.
 *
 * ⚠️ **TWO of those are 409s that share their STATUS with the one-live-envelope
 * 409 and mean something else entirely.** Both are matched on `error.code`
 * BEFORE the generic `status === 409` branch, which would otherwise tell
 * somebody to go and void an envelope that does not exist. Match the code,
 * never the status — and lift every future named 409 out of that branch too.
 *
 * ⚠️ **There is deliberately NO pre-check that hides or disables Send when the
 * Xero link reads `conflict`.** The backend lets `conflict` through: it means
 * Xero's copy is NEWER than our baseline, so the document is present, readable
 * and authoritative. That status exists to stop us OVERWRITING an accountant's
 * edit — reading a PDF overwrites nothing. Refusing on it would block signature
 * on precisely the quotes somebody has most recently worked on.
 *
 * ── WHICH XERO DOCUMENT, AND WHY NOT `xeroQuoteNumber` ───────────────────
 *
 * Two things turn on this. It decides whether the server is asked to fetch at
 * all, and — when the sender is downloading the PDF by hand instead — it names
 * which Xero document to go and get; naming the wrong one puts another
 * customer's quote in front of a signer. So the question "is this quote in
 * Xero?" is answered off the LINK ROW (`linkOf(quote).id`), never off
 * `quote.xeroQuoteNumber`.
 *
 * `xeroQuoteNumber` cannot carry that decision, for two independent reasons:
 *
 *  - **It is written BEST-EFFORT.** The push does not fail if the write-back is
 *    refused, so it can be empty on a quote that is demonstrably in Xero.
 *    Treating empty as "not in Xero" is therefore wrong in the safe direction,
 *    and treating it as the only label is wrong in the loud one.
 *  - **It has survived an unlink.** Production held quote 6 (`unlinked`) and
 *    quote 9 (`synced`) both reporting `QU-0479` — quote 9's document. The
 *    stale-value half is fixed server-side now (quote 6 reads `null`), but the
 *    field's *authority* was never the thing that got fixed, and a client that
 *    reads it as authoritative is one rollback away from the same instruction.
 *
 * ⚠️ **And never `xeroQuoteId` instead.** It is writable and written by
 * NOTHING — `null` on every quote, including ones sitting in Xero. This repo
 * already shipped one dead branch keyed on it that never fired once.
 *
 * The number is still worth PRINTING when it is there — it is what somebody
 * types into Xero's search — so it is offered as a convenience ON TOP of the
 * link row's answer, never as the answer itself. Three readings result, and all
 * three are rendered below: in Xero with a number, in Xero without one, and not
 * in Xero at all. That last decides what this form can do at all — there is no
 * Xero document to fetch and none to download either, so the sender's own file
 * is the only source — and it is stated outright rather than implied by an
 * odd-looking reference.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────
 *
 * ⚠️ *SUPERSEDED 2026-08-31: "`fields` (template merge fields, keyed by tab
 * LABEL, capped at 50) is not offered. It is optional, it only applies to the
 * path that is not the real one, and an editor for it is its own feature.
 * Adding it means adding `fields.<label>` to the routed key list too."* It was
 * never offered and now cannot be: `fields` belongs to the template schema, and
 * this form no longer builds a template payload at all.
 *
 * The rule the sentence carried is the part worth keeping, because it applies to
 * every future key: **a key this form SENDS but does not RENDER a control for
 * must be listed in `notRenderedKeys` below**, or its 422 is marked on nothing
 * while still counting towards "one field needs attention, highlighted above".
 */

/* ────────────────────────────────────────────────────────────────────────
 * The PDF size ceiling
 *
 * `documentBase64` is capped at 700,000 characters by the write schema. Base64
 * inflates by a third, so that is roughly a 512KB PDF. The cap is checked HERE,
 * against the file the user picked, so the refusal names the file and its size;
 * left to the server it is a 422 naming a field the user never typed into, and
 * left past that it is Express answering a bare 413 that names nothing at all.
 * ──────────────────────────────────────────────────────────────────────── */
const MAX_BASE64_CHARS = 700_000;
/** 4 base64 characters per 3 bytes, so this is the cap expressed in bytes. */
const MAX_PDF_BYTES = Math.floor((MAX_BASE64_CHARS * 3) / 4);

/**
 * `%PDF-` in base64. The first five characters are fixed whatever follows,
 * because the first three bytes always encode to `JVBE` and the fourth
 * character's leading bits always give `R`.
 *
 * The server checks the decoded bytes begin `%PDF-` and answers 422. Checking
 * it here as well costs nothing and turns "the server refused your document"
 * into "that is not a PDF" while the file picker is still in front of them.
 */
const PDF_MAGIC_BASE64 = 'JVBER';

/*
 * ⚠️ **THE ANCHORS ARE DERIVED, NOT ASKED FOR — owner's instruction,
 * 2026-08-31: "if things are derivable, derive them".**
 *
 * `anchorString` is the literal text DocuSign searches the PDF for to place the
 * signature box, and it has to match Xero's own Terms block **character for
 * character**. Two typed boxes stood here, one of them REQUIRED-looking and
 * pre-filled, and neither was a question anybody sending a quote can answer
 * better than this constant can: a person who edits it either reproduces this
 * value or breaks the send.
 *
 * ⚠️ **The date anchor's field is gone from the wire as well as the screen.**
 * `dateAnchorString` is optional and the server defaults it to `Date:`, and the
 * box was empty by default — so it was already being omitted on every send this
 * form has ever made. Removing it changes no request.
 *
 * The signature anchor is NOT optional server-side (2–120 chars), so this
 * constant is SENT on every document send. An override still exists and appears
 * only when DocuSign refuses the text — see `anchorOverride`, which is the one
 * moment the question is both answerable and worth asking.
 */
const DEFAULT_ANCHOR = 'Signature:';

/* ────────────────────────────────────────────────────────────────────────
 * The refusals the FETCH can produce
 *
 * ⚠️ **Described by what they are FOR, never by how many there are.** This
 * block was headed "The two refusals" and the header block above still says
 * "THE THREE REFUSALS" over a list of two — a count in prose beside a list is
 * the first thing to rot, and both were stale within one change.
 *
 * Matched on `error.code` and never on the message or the status — see the
 * header. TWO of them arrive as a 409, the same status as the one-live-envelope
 * refusal, and all three want different things from the reader.
 *
 * An unrecognised code is NOT treated as "not a refusal": it falls through to
 * the server's own message, which is the rule `xero.ts` states and the reason
 * a newer code renders as itself rather than as silence.
 * ──────────────────────────────────────────────────────────────────────── */
const QUOTE_NOT_IN_XERO = 'DOCUSIGN_QUOTE_NOT_IN_XERO';
const QUOTE_GONE_FROM_XERO = 'DOCUSIGN_QUOTE_GONE_FROM_XERO';

/**
 * The quote could not be moved out of DRAFT in Xero before its PDF was
 * fetched, so nothing was sent.
 *
 * ⚠️ **The refusal is the feature.** Xero stamps **DRAFT** across the PDF of a
 * draft quote, so the alternative to refusing is asking a customer to put their
 * signature on a document marked Draft. A send that "worked" and produced that
 * is the failure-looks-like-success shape this whole surface is built around.
 *
 * 🔴 **NOTHING ON THIS SCREEN FIXES IT, and the two controls that look like they
 * might are both wrong:**
 *
 *  - **NOT the file picker.** `DOCUSIGN_QUOTE_NOT_IN_XERO` is the one refusal
 *    recoverable inside the same request, and this is not it — the quote IS in
 *    Xero. Flipping `documentSource` to `upload` here would read as a solution
 *    and produce a send from the wrong source, which is strictly worse than the
 *    generic branch it would have replaced.
 *  - **NOT the anchor override.** DocuSign never saw a document; the anchor was
 *    never searched for. The two were never wired together and are not now.
 *
 * So this branch states the server's sentence and stops. **No recovery is
 * invented here**, because none was verified — the quote's status inside Xero
 * is not something this form, or any control in this app, can change.
 *
 * ⚠️ **DO NOT CONFUSE IT WITH `XERO_QUOTE_NOT_DRAFT`** in `@/lib/xero`, which is
 * its near-mirror and one word apart: that one refuses a PUSH because the quote
 * is **no longer** draft in Xero. This one refuses a SEND because it **still
 * is**. Opposite conditions, opposite remedies, almost the same name.
 */
const QUOTE_STILL_DRAFT_IN_XERO = 'DOCUSIGN_QUOTE_STILL_DRAFT_IN_XERO';

/*
 * ⛔ `type SendPath = 'document' | 'template'` was REMOVED 2026-08-31 with the
 * "What gets signed" toggle. There is one send path; a union of one is a
 * decision nobody makes wearing the shape of a decision somebody does.
 */

/**
 * Where the PDF comes from.
 *
 * `xero` sends neither `documentBase64` nor `documentName` and the server
 * fetches it. `upload` sends both, together.
 *
 * ⚠️ **STILL TWO VALUES, AND NO LONGER A CHOICE — see the header.** Nothing on
 * screen sets this: it is `xero` when the link row says the quote is in Xero,
 * `upload` when it is not, and `send()` flips it to `upload` on the
 * `DOCUSIGN_QUOTE_NOT_IN_XERO` 422. Collapsing it to a constant deletes the only
 * recovery that refusal has.
 */
type DocumentSource = 'xero' | 'upload';

/*
 * DERIVED, not redeclared. `DocuSignDelivery` is not exported from `@/lib/types`
 * today, and writing `'embedded' | 'email'` out again here would be a second
 * copy of a contract enum that this file does not own — it would go on
 * compiling the day a third mode is added, and silently stop offering it.
 * Reading it off the write schema's own key cannot drift.
 *
 * ⚠️ Read off `DocuSignDocumentEnvelopeInput` since 2026-08-31 (*SUPERSEDED:
 * `DocuSignTemplateEnvelopeInput['delivery']`*) — the template type is no longer
 * imported here. Same key, same union, and now the schema this form actually
 * sends, which is the one whose drift would matter.
 */
type DocuSignDelivery = NonNullable<DocuSignDocumentEnvelopeInput['delivery']>;

interface FormValues {
  /**
   * ⚠️ **NEVER EMPTY.** One blank entry at mount, or `initialSigner` when it was
   * handed one; every helper below preserves that, and the count control has a
   * floor of 1. An empty array would render a form with no signer field at all
   * and send a payload the server refuses.
   *
   * ⚠️ **ONE value per signer, not two strings.** Name and email are a pair: a
   * signer DocuSign can match needs both, and picking a contact fills both in one
   * change. See {@link SignerValue} for why the `contactId` rides along and why
   * it never reaches the wire.
   *
   * ⚠️ *SUPERSEDED 2026-09-01: `signer: SignerValue`.* An envelope may carry up
   * to `MAX_SIGNERS` people, on the owner's decision, and the write schema takes
   * `signers` as an ARRAY. The single-signer form is unchanged on screen — it is
   * this array with one entry.
   */
  signers: SignerValue[];
  /**
   * Addresses copied on the envelope. **MAY be empty, and usually is.**
   *
   * ⚠️ **PLAIN STRINGS, not `SignerValue`.** Owner decision: a copy asks for an
   * address and nothing else, because a copy name is rendered into the document
   * nowhere and DocuSign is given the address as the name server-side.
   * ⛔ Do not "harmonise" this into the signer shape — that puts a name box in
   * front of every colleague being copied, which is the friction the decision
   * removed. It is also what lets the picker's option `value` BE the address,
   * so there is no second copy of the selection to keep in step.
   *
   * 🔴 **EVERY ENTRY IS A TRIMMED, NON-EMPTY ADDRESS, 2026-09-17.**
   * *SUPERSEDED:* "⚠️ **Empty rows are ALLOWED to exist on screen and are
   * DROPPED on send.** Unlike `signers`, which is never empty and whose blank
   * row is a required field, a blank copy row is somebody who opened a row and
   * changed their mind. Refusing it would make Add a trap."
   *
   * The owner's multiselect replaced the row-per-copy shape that produced those
   * blanks. **Both writers now refuse one**: the picker returns option values
   * and values it was handed, and the typed box trims and will not add an empty
   * draft. ⛔ **Do not reintroduce an appender that seeds `''`** — the rules
   * that tolerated it are deleted in `validate` and `buildDocumentPayload`,
   * because a blank would now reach the wire rather than being filtered out of
   * it.
   */
  ccEmails: string[];
  /**
   * ⚠️ **FORCED to `email` while there is more than one signer**, and that is a
   * write to this value rather than a display-time override — see
   * {@link withSigners}. A remembered "preference" underneath a forced value is
   * a second answer to one question, and it comes back the moment the count
   * drops without anybody choosing it.
   */
  delivery: DocuSignDelivery;
  emailSubject: string;
  emailBlurb: string;
  /**
   * ⛔ `templateId` and `roleName` were REMOVED 2026-08-31 with the template
   * path. Neither key exists on the documents schema, which is `.strict()`, so
   * leaving them on this interface would have been two form values that could
   * only ever produce a 422.
   *
   * The ONLY typed value left, and it is typed only after DocuSign has refused
   * the derived one — see `DEFAULT_ANCHOR`.
   *
   * ⚠️ `documentName` and `dateAnchorString` were removed from this interface on
   * 2026-08-31, not merely hidden. The first is derived per quote and cannot
   * vary; the second was never sent. A form value nothing can set is a value
   * that can only be wrong.
   */
  anchorString: string;
}

const EMPTY: FormValues = {
  signers: [EMPTY_SIGNER],
  /*
   * ⚠️ **NOBODY COPIED AT MOUNT, which is the opposite of `signers` above.** A
   * signer is required, so the form opens with one empty row waiting to be
   * filled; a copy is not.
   *
   * ⚠️ *SUPERSEDED 2026-09-17, the last sentence only:* "The section opens as a
   * single Add control." It opens as an empty picker now. **The reasoning that
   * sentence served is unchanged** — the section must not imply an answer is
   * expected — and an unfilled picker showing a placeholder does not, where the
   * empty address box it was written against did.
   */
  ccEmails: [],
  /*
   * ⚠️ **`email`, on the owner's instruction, 2026-08-31.** *SUPERSEDED:
   * `'embedded'`.*
   *
   * The fork is real and costs something either way — see `DeliveryChoice`, which
   * states both consequences at once rather than swapping one for the other as
   * the value changes. What moved is which cost is the ordinary one: delivering a
   * link yourself means somebody has to be on the phone to the signer at the
   * moment it is minted, because it dies in about five minutes.
   *
   * ⚠️ **The default now writes to a CUSTOMER on success**, where it previously
   * wrote to nobody. That is why the confirmation dialog names the address, and
   * why it must keep doing so — this is the last screen on which the fork can be
   * reconsidered, and the reader has to be able to see that a real person is
   * about to be emailed.
   */
  delivery: 'email',
  emailSubject: '',
  emailBlurb: '',
  anchorString: DEFAULT_ANCHOR,
};

export interface SendForSignatureModalProps {
  quote: Quote;
  /**
   * A signer to open with, instead of the company's primary contact.
   *
   * Its one caller is **"Change signer and resend"**: that flow voids the live
   * envelope and then opens this dialog, and the whole point of the exercise is
   * the person it hands over. Without this the newly-chosen signer would be
   * discarded and the seed would refill the field with the primary contact — the
   * exact person somebody has just decided not to send to.
   *
   * ⚠️ **Omitted is NOT the same as empty.** `undefined` lets `SignerField` seed
   * the primary contact; an empty `SignerValue` would suppress that seed, because
   * the seed only fires into an untouched field.
   */
  initialSigner?: SignerValue;
  onClose: () => void;
  /**
   * The created envelope. The caller decides what happens next — on `embedded`
   * delivery something still has to mint the signing URL and put it in front of
   * the signer, and this dialog deliberately does not assume it owns that step.
   */
  onSent: (envelope: DocuSignEnvelope) => void;
}

/* ── who can be copied in ────────────────────────────────────────────── */

/**
 * A contact at this quote's company who can actually be copied: they have an
 * address.
 *
 * ⚠️ **Deliberately NOT `SignerOption`'s twin by import.** `SignerField` owns
 * its own row shape and fetches per signer ROW with its own search term; this
 * is one read for one control, and sharing a type across the two would invite
 * sharing the read, which they cannot — see the fetch below.
 */
interface CopyContactOption {
  id: number;
  name: string;
  email: string;
}

/**
 * The answer to one contacts read, stamped with the company it answers for.
 *
 * ⚠️ **`failed` is NOT the same as an empty list, and conflating them is the
 * one thing this shape exists to prevent.** Both render no options. Telling
 * somebody this company has nobody to copy, when the request simply did not come
 * back, sends them looking for a contact record that is already there — and on
 * THIS control it would also read as "there is no way to copy anyone in", which
 * is never true: the typed address below the picker is always available.
 */
interface CopyContacts {
  /** So a list belonging to a company we are no longer showing is dropped. */
  companyId: number;
  options: CopyContactOption[];
  failed: boolean;
}

/* ── small presentational pieces ─────────────────────────────────────── */

/*
 * The blocks that must be read are the shared `Notice` primitive. `role` is
 * still the caller's choice at every call site: `alert` for the sandbox
 * warning, which is news about correctness, and `status` for a standing fact
 * about the action rather than something that just went wrong.
 */

/** One labelled fact about the quote being sent. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}

/**
 * The delivery choice.
 *
 * Two radio cards rather than a checkbox, and both consequences on screen at
 * once rather than one that swaps as the value changes. This is a real decision
 * with a real cost either way — `email` means DocuSign emails the customer
 * directly and there is then no signing URL to mint, ever; asking for one
 * afterwards is a `409`. A checkbox labelled "email the signer" would let
 * somebody take that fork without reading what it forecloses.
 *
 * Native radios: the design system has no exclusive-choice primitive, and a
 * native group brings arrow-key navigation, the roving tab stop and the mobile
 * hit target with it. Nothing here re-implements an input.
 */
function DeliveryChoice({
  name,
  value,
  onChange,
  disabled,
  lockedToEmail,
}: {
  name: string;
  value: DocuSignDelivery;
  onChange: (next: DocuSignDelivery) => void;
  disabled: boolean;
  /**
   * More than one signer, so `email` is the only mode this envelope can have.
   *
   * ⚠️ **The reason is rendered as TEXT, never a `title`.** A disabled control
   * has `pointer-events: none`, so its tooltip never reaches the person it was
   * written for — the same rule the panel's denial reasons follow.
   */
  lockedToEmail: boolean;
}) {
  /*
   * ⚠️ **EMAIL LEADS AND EMAIL IS THE DEFAULT — owner's instruction,
   * 2026-08-31**, reversing `embedded`. The order follows the default rather than
   * being left where it was: a radio group whose second option is the selected one
   * reads as a form somebody has already changed.
   *
   * ── THE COPY IS EXACT, AND TWO SENTENCES WERE CUT ────────────────────────
   *
   * ⛔ REMOVED: *"DocuSign emails the address itself."* — the plain descriptive
   * half of the email consequence. It restated the option's own label.
   *
   * ⛔ REMOVED, from the signer email field: *"DocuSign matches on this
   * exactly."* — moot now the address comes from a picked contact rather than
   * from typing. The field it hung on no longer exists; `SIGNER_NEEDS_EMAIL` in
   * `@/lib/docusign` carries the surviving half of that fact, where the picker
   * needs it.
   *
   * ✅ KEPT, VERBATIM, and both are on the protected list — the no-helper-text
   * prohibition exempts a refusal and anything naming an irreversible
   * consequence, and each of these names a state that is invisible until
   * somebody has already hit it:
   *
   *   "Single-use link, expires in about five minutes. Generate it with the
   *    signer present."   — a link that died five minutes ago looks like DocuSign
   *                         being slow, not like a link that expired.
   *   "No signing link can be minted afterwards."
   *                       — choosing email and then finding no link can be minted
   *                         looks like a broken button. It is a `409`, for ever,
   *                         and the only recovery is a fresh envelope.
   */
  const options: { value: DocuSignDelivery; label: string; consequence: string }[] = [
    {
      value: 'email',
      label: 'DocuSign emails the signer',
      consequence: 'No signing link can be minted afterwards.',
    },
    {
      value: 'embedded',
      label: 'I deliver the link myself',
      consequence:
        'Single-use link, expires in about five minutes. Generate it with the signer present.',
    },
  ];

  return (
    <fieldset className="min-w-0 border-0 p-0" disabled={disabled}>
      <legend className="mb-1.5 text-xs font-medium text-fg-secondary">
        How the signer receives it
      </legend>
      <div className="grid gap-2">
        {options.map((option) => {
          const checked = value === option.value;
          /*
           * ⚠️ Locked out on the OPTION, not on the fieldset. The fieldset's own
           * `disabled` is the submitting state and takes both; this takes only
           * the one mode a multi-signer envelope cannot have, leaving `email`
           * selectable so the group still reads as a live control rather than a
           * dead one.
           */
          const unavailable = lockedToEmail && option.value !== 'email';
          return (
            <label
              key={option.value}
              className={[
                'flex min-w-0 items-start gap-2.5 rounded-md border px-3 py-2.5',
                'transition-colors duration-150 ease-out motion-reduce:transition-none',
                checked
                  ? 'border-fg-secondary bg-surface-active'
                  : 'border-border bg-surface hover:border-hairline-strong',
                disabled || unavailable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={unavailable}
                onChange={() => onChange(option.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent-brand)]"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-fg">{option.label}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">
                  {option.consequence}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {/*
        Why the second option is unavailable, and the way back to it. On screen
        rather than on a `title`, and one of the cases the no-helper-text rule
        exempts: it explains a refusal the reader is looking at.
      */}
      {lockedToEmail && (
        <p className="m-0 mt-2 text-xs leading-relaxed text-fg-muted [overflow-wrap:anywhere]">
          {MULTIPLE_SIGNERS_ARE_EMAILED}
        </p>
      )}
    </fieldset>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma === -1 ? '' : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

function approximateKb(bytes: number): string {
  return `${Math.round(bytes / 1024).toLocaleString()}KB`;
}

/* ── the signer list ─────────────────────────────────────────────────── */

/**
 * A row nobody has put anything in — so removing it discards nothing.
 *
 * ⚠️ **`contactId` is tested as well as the two strings.** A picked contact
 * whose name and address were then cleared by hand still holds an id, and
 * treating that as blank would throw away a selection somebody made.
 */
function isBlankSigner(signer: SignerValue): boolean {
  return signer.contactId === null && signer.name.trim() === '' && signer.email.trim() === '';
}

/**
 * The signer list, and the delivery those signers permit, written together.
 *
 * ⚠️ **The two cannot be set apart from each other.** Above one signer there is
 * no signing link to hand over — a link is single use and names one recipient —
 * so any change to the list is also a decision about delivery. Doing it here
 * means every caller that adds, removes or resizes gets it, rather than each one
 * remembering; see {@link MULTIPLE_SIGNERS_ARE_EMAILED}.
 *
 * ⚠️ **Dropping back to one signer does NOT flip delivery back.** `email` stays
 * selected, visibly, and the person may change it. Restoring a value they last
 * set several actions ago would be this form quietly choosing for them at the
 * one point where the choice writes to a customer.
 */
function withSigners(current: FormValues, signers: SignerValue[]): FormValues {
  return {
    ...current,
    signers,
    delivery: signers.length > 1 ? 'email' : current.delivery,
  };
}

/**
 * The row this form opens with — the only one the company's primary contact is
 * ever seeded into.
 *
 * ⚠️ **A row key, deliberately, and NOT "whichever row is first right now".**
 * Tied to the position, the seed comes back to bite: fill in signer 1 with the
 * seeded primary contact, add a blank signer 2, remove signer 1 — and the blank
 * row, now first, fills itself with the person who was just removed. Tied to the
 * row's own identity, a removed seed row stays removed, and no other row ever
 * seeds however the list is rearranged.
 */
const FIRST_ROW_KEY = 0;

/** One signer as the confirmation names them — never a bare name. */
function signerSummary(signer: SignerValue): string {
  return `${signer.name.trim() || 'the signer'} (${signer.email.trim() || 'no address yet'})`;
}

/**
 * The keys this form actually RENDERS a control for, so a 422 can be routed to
 * one — derived from the payload rather than written out, so the two cannot
 * drift, then narrowed by the keys that are sent but have no control.
 *
 * `quoteId` is why the narrowing exists at all. It is sent on both paths and
 * comes from the record, not from anything the user typed, so a 422 naming it
 * must reach the form-level banner. Routed to a "field" it would vanish —
 * marked on a control that is not on screen, still counted in the error total,
 * highlighting nothing. That is the same silent failure an empty path used to
 * cause, arrived at from the opposite direction.
 */
/*
 * ⚠️ **THE LIST GREW WITH EVERY DERIVED FIELD, and it had to.** `documentName`
 * is sent on an upload and has no control any more; `anchorString` is sent on
 * every document send and has one only after DocuSign has refused it. A key
 * routed to a "field" that is not on screen is marked on nothing, still counted
 * in "one field needs attention, highlighted above", and therefore invisible —
 * the exact silent failure this narrowing exists to prevent. **Derive a field
 * and you MUST add it here, or its 422 disappears.**
 *
 * It is a function rather than a constant because one entry is conditional: the
 * anchor input really is on screen once the override is open, and excluding it
 * then would throw away a refusal the user can act on.
 */
function notRenderedKeys(anchorVisible: boolean): readonly string[] {
  return anchorVisible
    ? ['quoteId', 'documentName']
    : ['quoteId', 'documentName', 'anchorString'];
}

/*
 * ⚠️ **WIDENED 2026-09-01 TO DESCEND INTO ARRAYS, and the old version would have
 * broken SILENTLY the moment `signers` existed.**
 *
 * *SUPERSEDED, the whole walk:* it went **one level deep** and explicitly
 * skipped arrays — `if (value !== null && typeof value === 'object' &&
 * !Array.isArray(value))` — under the comment *"one level deep is enough:
 * `signer` is the only object either schema declares, and zod reports its issues
 * as `signer.email`."* Both halves were true of a single `signer` OBJECT and
 * neither is true of a `signers` ARRAY.
 *
 * `ApiError.routeFieldErrors` matches a path against this list **exactly**, so a
 * server 422 on `signers.0.email` would have found no match, landed in
 * `unroutable`, and rendered as an unattached form message — marked on no
 * control, while still counting towards "one field needs attention, highlighted
 * above". Nothing errors; the refusal simply stops pointing at the box that
 * caused it.
 *
 * **Every non-array key behaves exactly as before**: the key itself, plus one
 * level of its own keys. Arrays add `signers.0` and `signers.0.email`-shaped
 * keys, built the same way {@link signerErrorKey} builds them, so the two halves
 * of this file cannot drift on the spelling.
 *
 * ⚠️ It stops at an array's element and that element's own keys — deeper still
 * would be inventing paths for shapes neither schema declares.
 */
function knownWriteKeys(payload: Record<string, unknown>, notRendered: readonly string[]): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (notRendered.includes(key)) continue;
    keys.push(key);
    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        // The index itself: a 422 can name the WHOLE entry rather than a field
        // on it, and the form renders a message per row for exactly that.
        keys.push(`${key}.${index}`);
        if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return;
        for (const nested of Object.keys(entry as Record<string, unknown>)) {
          keys.push(`${key}.${index}.${nested}`);
        }
      });
      continue;
    }
    if (value !== null && typeof value === 'object') {
      for (const nested of Object.keys(value as Record<string, unknown>)) {
        keys.push(`${key}.${nested}`);
      }
    }
  }
  return keys;
}

/* ── which Xero document, if any ─────────────────────────────────────── */

/*
 * ⚠️ **`linkOf(quote).id` is the only "is this in Xero?" test.** It falls back
 * to `NO_LINK`, so a response that predates the envelope reads as "no
 * counterpart" rather than crashing.
 */

/**
 * What this form may tell somebody to go and download.
 *
 * `inXero` comes from the link row's `id` and from nothing else — see the
 * header. `number` is a **convenience carried alongside it**, not a fallback
 * for it: it is read only once `inXero` is already true, so an empty one can
 * never be silently replaced by an identifier that means something else.
 *
 * Deliberately NOT a `string | null` that collapses the three states into one.
 * A single label would force the caller into `xeroQuoteNumber ?? ourLabel`,
 * which is exactly the bug this replaces — our own reference reads like a Xero
 * one in that sentence, and nothing on screen says which it is.
 */
type XeroDocumentRef = { inXero: false } | { inXero: true; number: string | null };

function xeroDocumentRef(quote: Quote): XeroDocumentRef {
  if (linkOf(quote).id === null) return { inXero: false };
  const number = quote.xeroQuoteNumber?.trim();
  return { inXero: true, number: number ? number : null };
}

/**
 * The envelope's default filename — what the signer sees in DocuSign's document
 * list, and what they keep afterwards.
 *
 * ⚠️ `??` is right HERE and wrong in the instruction above it. This is a
 * ladder of three names for ONE record, where each rung is a strictly worse
 * label for the same thing; the instruction is two different records' numbers,
 * where the lower rung is not a worse label but a **wrong document**.
 *
 * `quoteNumber` leads because it is OURS and UNIQUE — it can never name another
 * quote. `xeroQuoteNumber` is reached only once the LINK ROW says this quote is
 * in Xero, the same gate and the same reason as the instruction.
 * `Quote-{id}` is the floor and is always unambiguous.
 */
function defaultDocumentName(quote: Quote, ref: XeroDocumentRef): string {
  const xeroNumber = ref.inXero ? ref.number : null;
  return `${quote.quoteNumber ?? xeroNumber ?? `Quote-${quote.id}`}.pdf`;
}

/**
 * The reference a CUSTOMER reads — `{{quoteNumber}}` in the subject and the
 * message.
 *
 * 🔴 **`xeroQuoteNumber` LEADS, and `quoteNumber` is deliberately not consulted
 * at all.** Owner's decision, and it is a measurement rather than a preference:
 * `d_quotes.quote_number` is **NULL on every quote in production** while
 * `xero_quote_number` is populated on all but a couple, so the ladder in
 * {@link defaultDocumentName} — which leads with `quoteNumber` because a
 * filename must never name another record — resolves to its FLOOR on every
 * quote there is. That is why the owner was shown `#426`: not a bug, the third
 * rung of a ladder whose first two rungs were empty.
 *
 * ⚠️ **This is the SERVER's own precedence for the PDF filename**, so the
 * reference in the email and the name of the document attached to it agree.
 *
 * ⚠️ **The number is read only once the LINK ROW says this quote is in Xero**,
 * which is the same gate {@link xeroDocumentRef} applies and for the same
 * reason — see the header. `xeroQuoteNumber` has survived an unlink before
 * (quote 6 advertising quote 9's `QU-0479`), and the cost of reading a stale one
 * HERE is worse than anywhere else in this form: it prints **another customer's
 * quote reference in an email to this one**. An unlinked quote falls to the id,
 * which is ours and can never name a different record.
 */
function signatureQuoteRef(quote: Quote, ref: XeroDocumentRef): string {
  const xeroNumber = ref.inXero ? ref.number : null;
  return xeroNumber ?? `#${quote.id}`;
}

/**
 * DocuSign's own objection to the anchor text, which is the one refusal worth
 * repeating word for word: it names the string it searched for, and no sentence
 * of ours can carry that.
 *
 * Matched on `error.code` first. The message test is a fallback for a server
 * that forwards DocuSign's error without lifting the code out — the same
 * "unrecognised means newer than this map, never not-a-refusal" rule the Xero
 * model states.
 */
const ANCHOR_NOT_FOUND = 'ANCHOR_TAB_STRING_NOT_FOUND';

function isAnchorRefusal(error: ApiError): boolean {
  return error.code === ANCHOR_NOT_FOUND || error.message.includes(ANCHOR_NOT_FOUND);
}

/* ── the modal ───────────────────────────────────────────────────────── */

export function SendForSignatureModal({
  quote,
  initialSigner,
  onClose,
  onSent,
}: SendForSignatureModalProps) {
  const formId = useId();
  const deliveryName = useId();

  /*
   * Who the signer is hearing from — `{{senderName}}`.
   *
   * ⚠️ **Rendered defensively, because `User.name` can be `''`.** This app holds
   * records whose name is an empty string, and an empty string in a signature
   * line reads as a broken template rather than as a missing name — so the
   * address stands in, which is at least something a customer can reply to.
   * Both are empty only when nobody is signed in, which this dialog cannot
   * reach; the token then fills with nothing rather than the word "undefined".
   *
   * ⚠️ **`user` is the TARGET during a support session, and that is correct
   * here** — the envelope is created by whoever the request runs as, so the
   * name on it must be theirs. See `AuthContextValue.user`.
   */
  const { user, ability } = useAuth();
  const senderName = user ? user.name.trim() || user.email.trim() : '';

  /*
   * ⚠️ **`initialFocusRef` went with the two signer inputs, deliberately.** It
   * pointed at the Signer name box, which no longer exists — the signer is a
   * Combobox now, and focusing a picker on open pops its listbox over the sandbox
   * warning, which is the one thing on this screen that must be read first.
   * `Modal` focuses its own panel with nothing named, which is the right
   * behaviour here rather than a fallback.
   */

  /*
   * Which Xero document — if any — this quote has. Decided by the link row, and
   * used by BOTH the instruction and the default filename, so the two cannot
   * name different documents.
   */
  const xeroRef = xeroDocumentRef(quote);

  /*
   * The two facts about this quote that the subject and the message quote back
   * to the customer. Both are read from what the form already holds — there is
   * deliberately no extra request for either.
   */
  const quoteRef = signatureQuoteRef(quote, xeroRef);
  // The Billing name: a quote is a Xero document (owner 2026-10-25).
  const companyName = quote.company ? documentCustomerName(quote.company) : '';
  /*
   * The company whose contacts may be offered — to the signer rows AND to the
   * copy picker, from ONE derivation so the two cannot end up scoped to
   * different accounts.
   *
   * ⚠️ **`null` is an ordinary state, not a fault.** `d_quotes.company_id` is
   * nullable, and a quote naming no company offers NOTHING rather than the
   * unscoped directory of every contact in the CRM — the rule
   * `SignerFieldProps.companyId` already states. It does not stop a send: a
   * signer and a copy can both be typed by hand.
   */
  const companyId = quote.company?.id ?? null;

  /**
   * Fill one template with everything this quote, this sender and this signer
   * know.
   *
   * ⚠️ **The substitution itself is NOT implemented here.** `fillTemplate` in
   * `@/lib/docusign-templates` owns it, because the same four tokens are
   * described to an administrator on the Settings screen that edits these
   * templates — two implementations would agree the day they were written and
   * drift on the first token added, and what would drift is the text of an
   * email a customer receives.
   *
   * ⚠️ **`null` IS PASSED THROUGH, NEVER PAPERED OVER HERE.** `fillTemplate`
   * owns what an unknown token degrades to — `signerFirstName` becomes
   * `there`, so an unaddressed greeting reads `Hi there,` rather than `Hi ,` —
   * and the other three close their own whitespace seam and vanish. Substituting
   * a fallback of our own at this call site would be a second copy of that
   * decision, and the one a customer reads.
   */
  function fillSignatureTemplate(template: string, firstName: string | null): string {
    return fillTemplate(template, {
      signerFirstName: firstName,
      quoteNumber: quoteRef,
      companyName,
      senderName,
    });
  }

  /*
   * ⛔ `const [path, setPath] = useState<SendPath>('document')` was REMOVED
   * 2026-08-31 with the template toggle. Every send is the document path.
   */

  /*
   * The source, DECIDED rather than chosen — see the header. Fetching is what
   * happens the moment the link row says this quote is in Xero; not in Xero
   * means there is nothing to fetch, so `upload` is the only thing left. The
   * setter survives for exactly one caller: `send()`, on the 422.
   */
  const [documentSource, setDocumentSource] = useState<DocumentSource>(
    xeroRef.inXero ? 'xero' : 'upload',
  );
  /*
   * ⚠️ **The signer is no longer seeded from `quote.contact` here**, and that is
   * a deliberate move rather than a loss. `SignerField` seeds the COMPANY's
   * designated primary contact through `primaryContactSeed`, which is the one
   * module three form modals already share for that decision — and it seeds only
   * a contact that HAS an email, which `quote.contact` (a bare `{id, name}`) can
   * say nothing about. A name with no address is a signer DocuSign cannot match.
   *
   * `initialSigner` overrides the seed entirely; see the prop.
   */
  const [values, setValues] = useState<FormValues>(() => ({
    ...EMPTY,
    /*
     * ⚠️ ONE entry, not an empty list. `initialSigner` is the person somebody
     * has just chosen in "Change signer and resend", so it opens as signer 1 and
     * the seed in `SignerField` stays suppressed for a field that is not
     * untouched. Omitting the key entirely — not passing `[]` — is what lets
     * that seed fire on an ordinary send.
     */
    ...(initialSigner ? { signers: [initialSigner] } : {}),
    /*
     * ⚠️ **SEEDED SYNCHRONOUSLY, from the BUILT-IN default, and the admin
     * template replaces it only once it arrives.** *SUPERSEDED, the literal that
     * stood here: `` `Please sign your SEED quote ${quote.quoteNumber ?? `#${quote.id}`}` ``
     * — same sentence, but written out at one call site and reading
     * `quote.quoteNumber`, which is NULL on every quote in production.*
     *
     * 🔴 **The alternative — waiting for the template read — would open the
     * dialog with a BLANK subject on a field that has never been blank**, and a
     * subject box that fills itself a moment later reads as the form correcting
     * a mistake. The admin template is an OVERRIDE of this text, not the source
     * of it, so there is always something honest to show while it is in flight.
     *
     * ⚠️ **The MESSAGE is seeded here too, which it never was before.** It could
     * not have been while its text was a literal: the greeting needs a name, and
     * at this instant there is usually nobody to greet. It can be now because
     * `fillTemplate` degrades `{{signerFirstName}}` to `there` — so the box opens
     * holding a message that is already sendable, and refines to the signer's own
     * name the moment `SignerField` seeds the primary contact.
     */
    emailSubject: fillSignatureTemplate(
      DEFAULT_SIGNATURE_SUBJECT,
      initialSigner ? signerFirstName(initialSigner.name, null) : null,
    ).trim(),
    emailBlurb: fillSignatureTemplate(
      DEFAULT_SIGNATURE_MESSAGE,
      initialSigner ? signerFirstName(initialSigner.name, null) : null,
    ).trim(),
  }));

  /* ── the seeded subject and message ──────────────────────────────────
   *
   * Owner's instruction: both fields arrive filled in, the greeting carrying the
   * signer's first name, and both driven by a template an administrator edits in
   * Settings.
   *
   * ── THE ONE RULE ─────────────────────────────────────────────────────────
   *
   * 🔴 **WHILE A FIELD IS UNTOUCHED IT *IS* THE DERIVATION. THE MOMENT SOMEBODY
   * TYPES IN IT, NOTHING WRITES TO IT AGAIN.** Not the admin template landing,
   * not the signer changing, not the signer count changing — nothing, for the
   * life of the dialog.
   *
   * That is the same rule as the seeded document name and the seeded signer in
   * `SignerField`, and it is the whole of the protection, because **two of the
   * three inputs arrive over a REQUEST and therefore land AFTER first paint**:
   * the admin template, and the primary contact the signer seeds from. Without
   * it, a message somebody had begun typing would be silently rewritten a second
   * after they started — which is not a state they can be expected to notice, on
   * text that then goes to a customer.
   *
   * ⚠️ **THE FLAGS ARE REFS, NOT STATE, AND THAT IS WHAT MAKES THE ORDER SAFE.**
   * The effect reads `.current` at the moment it runs, so a touch registered at
   * any point before the late arrival is already visible to it. A state flag
   * would be read from the render that scheduled the effect, which is the frame
   * BEFORE the keystroke — so a template arriving in the same tick as the first
   * character typed would win, exactly once, and only on the unluckiest timing.
   *
   * ⚠️ **SET EVEN WHEN THE BOX IS EMPTIED**, which is the half that looks
   * wrong and is the point: if clearing the field left it "untouched" it would
   * hand control back to the seeder, the derivation would refill it on the next
   * input that changed, and the field would fight the person trying to empty it.
   * An empty box somebody deliberately emptied is a decision; it is also a legal
   * send, because `sharedPayload` omits an empty optional rather than sending
   * `''`.
   * ──────────────────────────────────────────────────────────────────── */

  /**
   * The admin-editable templates, or `null` while the read is in flight — and
   * `null` for ever if it failed.
   *
   * ⚠️ **A FAILED READ AND A SERVER SAYING `null` COLLAPSE TO THE SAME
   * HANDLING, and here that is correct rather than the usual `??` defect.**
   * Everywhere else in this codebase a coalesce that folds *nobody looked* into
   * *we looked and found nothing* destroys a fact; this field has no such fact
   * to destroy. `subject: null` means **use the built-in default** — the server
   * is not reporting a gap, it is declining to override — and a settings request
   * that failed leaves the built-in default as the only honest thing to show.
   * ⛔ **Never leave either field blank because this read failed.**
   *
   * ⛔ **And the resolution is `resolveTemplate`, NEVER `??`** — see its own
   * doc. Four values have to land on the default and only one of them is
   * `null`: a server predating the key sends `undefined`, and an empty or
   * whitespace-only stored value would otherwise be sent as the SUBJECT LINE of
   * a customer's email.
   */
  const [templates, setTemplates] = useState<DocuSignEmailTemplates | null>(null);

  /**
   * Has a person put their own text in this field?
   *
   * ⚠️ **Refs rather than state, deliberately — see the block above.** Nothing
   * renders off these, so a touch costs no render, and the value the seeding
   * effect reads is always the current one.
   */
  const subjectTouched = useRef(false);
  const blurbTouched = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      /*
       * ⚠️ **Resolves to the templates THEMSELVES, not to an envelope** — it is
       * one of the few `api` methods that unwraps for the caller, so there is no
       * `.data` here. The connection read directly above takes `result.data`,
       * and the two sitting a few lines apart is exactly where somebody copies
       * the wrong one.
       */
      .docusignTemplates(signal)
      .then((result) => {
        if (!signal.aborted) setTemplates(result);
      })
      .catch(() => {
        /*
         * Deliberately silent, and deliberately NOT an error state. Nobody asked
         * for a template; they asked to send a quote. The built-in defaults are
         * already on screen, so there is nothing to recover and nothing a reader
         * could do about it — a banner here would report a failure whose whole
         * consequence is that some wording is the standard wording.
         */
        if (!signal.aborted) setTemplates(null);
      });
    /*
     * ⚠️ **No `.finally` and no loading flag, matching the connection read
     * above.** Nothing on screen waits for this: the built-in defaults are
     * already in the boxes, so there is no state between "in flight" and
     * "arrived" for a reader to be told about. A flag nothing renders is a flag
     * that gets rendered later by somebody who assumes it was meant to be.
     */
    return () => controller.abort();
  }, []);

  /*
   * The signer the greeting addresses.
   *
   * 🔴 **EXACTLY ONE SIGNER, OR NOBODY IS NAMED — `null`, which greets the
   * envelope as `Hi there,`.** All signers receive the SAME message body; that
   * is what a DocuSign envelope is. So on a two-signer envelope a seeded
   * "Hi Anjali," is read by somebody who is not Anjali, in an email asking them
   * to sign a contract. Handing `fillTemplate` a `null` above one signer is not
   * a degradation here, it is the CORRECT greeting for several recipients — and
   * it falls out of the token's own fallback rather than out of a sentence
   * written here.
   *
   * ⚠️ **The second argument is `null`, and that is a NAMED GAP rather than a
   * value.** `signerFirstName` prefers a contact's stored `firstName` — which
   * exists precisely because splitting a name is a guess that goes wrong on
   * `Dr. A. R. Menon` — and this dialog has no contact record to read one from:
   * a `SignerValue` carries `{contactId, name, email}`, and `SignerField`
   * fetches the contacts per row and reports the value up without them. So the
   * typed-name branch is the one that runs, on every send, today.
   *
   * **The seam that closes it is `SignerField` reporting the chosen contact
   * upward** — not a second `/contacts` read here, which would duplicate one
   * already being made a row at a time, and not a guess at the name's shape,
   * which is the thing `firstName` exists to avoid.
   */
  const soleSigner = values.signers.length === 1 ? values.signers[0] : null;
  const greetingName = soleSigner ? signerFirstName(soleSigner.name, null) : null;

  /*
   * ⚠️ **Both fields derive from the moment the dialog opens, and neither is
   * ever blank.** `resolveTemplate` supplies the shipped wording whenever the
   * administrator has configured none — which includes the read still being in
   * flight, and the read having failed.
   */
  const derivedSubject = fillSignatureTemplate(
    resolveTemplate(templates?.subject, DEFAULT_SIGNATURE_SUBJECT),
    greetingName,
  ).trim();
  const derivedMessage = fillSignatureTemplate(
    resolveTemplate(templates?.message, DEFAULT_SIGNATURE_MESSAGE),
    greetingName,
  ).trim();

  useEffect(() => {
    setValues((current) => {
      const next: Partial<FormValues> = {};
      if (!subjectTouched.current && current.emailSubject !== derivedSubject) {
        next.emailSubject = derivedSubject;
      }
      if (!blurbTouched.current && current.emailBlurb !== derivedMessage) {
        next.emailBlurb = derivedMessage;
      }
      /*
       * ⚠️ **The SAME object back when nothing moved, which is what stops this
       * looping.** The effect writes `values`, and `derivedMessage` is computed
       * from `values.signers` — so a new object on every run would re-render, and
       * a re-render that produced identical derivations would still have rewritten
       * state for no reason. Returning `current` ends it in one pass.
       */
      return Object.keys(next).length === 0 ? current : { ...current, ...next };
    });
  }, [derivedSubject, derivedMessage]);

  /*
   * Why the count control stopped short of what was asked for. Its own state
   * rather than a form error: nothing is wrong with the form, and the sentence
   * describes an action that was declined rather than a value that is invalid.
   * See `setSignerCount`.
   */
  const [countNotice, setCountNotice] = useState<string | null>(null);

  /*
   * A stable identity per signer ROW, parallel to `values.signers`.
   *
   * 🔴 **`key={index}` is wrong here, and wrong in a way that looks fine.** A
   * `SignerField` owns state the value does not carry — which mode it is in
   * (contact picker vs hand-typed), its search term, its loaded contacts — and
   * React keeps that state with the KEY. Keyed by index, removing signer 1 of
   * three leaves the instance at position 0 mounted while signer 2's value slides
   * into it: a hand-typed person can land in an instance sitting in picker mode,
   * showing "Choose a contact…" above an address that is already filled in.
   * Nothing errors and no value is lost — it just reads as a selection that
   * failed to take.
   *
   * ⚠️ **Two arrays that must stay the same length**, which is the cost of this.
   * Only the three functions below ever change either, and each changes both in
   * the same call; the render falls back to the index if they ever disagree, so a
   * drift is a cosmetic remount rather than a crash.
   */
  const [rowKeys, setRowKeys] = useState<number[]>(() => [0]);
  const nextRowKey = useRef(1);

  /*
   * The name the signer sees on the document in DocuSign. DERIVED and constant
   * for this quote — it was a required text box pre-filled with exactly this
   * value, which is a question whose only right answer the form already held.
   *
   * Still SENT, and it has to be: `documentBase64` with no `documentName` is a
   * 422 on `documentName`, because absent-and-absent is what asks the server to
   * fetch from Xero. Removing the control is not removing the key.
   */
  const documentName = defaultDocumentName(quote, xeroRef);

  /*
   * ⚠️ **The anchor override, and it opens only on a REFUSAL.**
   *
   * DocuSign answers `ANCHOR_TAB_STRING_NOT_FOUND` naming the string it searched
   * for, which is the one moment the question "what text is in this PDF?" is
   * both answerable and worth putting to a person — and on an uploaded PDF,
   * which need not carry Xero's Terms block at all, it is the only recovery
   * there is. Before that it is a box whose correct value is a constant.
   */
  const [anchorOverride, setAnchorOverride] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [documentBase64, setDocumentBase64] = useState<string>('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [readingFile, setReadingFile] = useState(false);

  /*
   * ── PREVIEW, BEFORE IT GOES (owner 2026-10-25) ──────────────────────────
   *
   * The sender sees exactly what is about to be signed. TWO viewers, one per
   * SOURCE, because the two sources are two different documents:
   *
   *   xero    `QuotePdfPreviewModal` — the shared viewer, fetching the PDF Xero
   *           holds through `GET /quotes/:id/pdf/preview` (a pure read: no
   *           stage change, no Xero write). `hideOpenQuote`, because leaving
   *           for the quote page would drop this half-filled form.
   *   upload  the chosen FILE itself, over a blob URL in a plain frame. It is
   *           the bytes `documentBase64` was read from, so it is what goes.
   *
   * ⚠️ **"Send for signature" inside the shared viewer RETURNS HERE.** The
   * viewer always offers that action — with no handler it links to
   * `/quotes/:id?sign=1`, which would navigate away from this very form and lose
   * everything typed. The handler closes the preview, which is where the person
   * already is in the flow. Not a no-op: it is the one correct destination.
   *
   * Stacking: both viewers are `Modal`s portalled AFTER this one at the same
   * `z-50`, so DOM order puts them on top; each `Modal` hands focus back to the
   * Preview button on close, and stops Escape from closing this dialog too.
   */
  const [previewTarget, setPreviewTarget] = useState<QuotePdfPreviewTarget | null>(null);
  /*
   * The uploaded file's object URL: minted on open, revoked on close, and on
   * unmount through the ref (the send dialog can close with the preview up).
   * The file cannot change while it is open — the preview covers the picker.
   */
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const filePreviewUrlRef = useRef<string | null>(null);
  function closeFilePreview() {
    if (filePreviewUrlRef.current) URL.revokeObjectURL(filePreviewUrlRef.current);
    filePreviewUrlRef.current = null;
    setFilePreviewUrl(null);
  }
  useEffect(
    () => () => {
      if (filePreviewUrlRef.current) URL.revokeObjectURL(filePreviewUrlRef.current);
    },
    [],
  );

  const [connection, setConnection] = useState<DocuSignConnection | null>(null);
  /*
   * ⛔ `templates`, `templatesError` and `templatesLoading` were REMOVED
   * 2026-08-31 with the template path. `GET /docusign/templates` costs one
   * upstream DocuSign call PER TEMPLATE, so this dialog now spends none of that
   * quota at all — it was already gated behind the click that chose the path,
   * and that click no longer exists.
   */

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formMessages, setFormMessages] = useState<string[]>([]);
  const [anchorRefusal, setAnchorRefusal] = useState<string | null>(null);
  /*
   * A refusal about the FETCH, kept apart from `formMessages` because the two
   * say different things about what to do next. A form-level banner is "nothing
   * was sent"; this is "nothing was sent AND here is the source to use
   * instead", and in the `not-in-xero` case the control that fixes it has just
   * been switched on underneath it.
   */
  const [fetchRefusal, setFetchRefusal] = useState<{
    kind: 'not-in-xero' | 'gone';
    message: string;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const errors = { ...clientErrors, ...serverErrors };

  /*
   * The connection, only to decide the not-binding warning.
   *
   * Every failure — including the 403 a non-admin sender gets, which is the
   * COMMON case here — leaves `connection` null, and null warns. There is
   * deliberately no error state and no retry: the read is not a prerequisite
   * for sending, and a person who cannot read it can still send.
   */
  useEffect(() => {
    const controller = new AbortController();
    api
      .docusignConnection(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setConnection(result.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setConnection(null);
      });
    return () => controller.abort();
  }, []);

  /* ── who else gets it: the contacts this quote's company has ─────────
   *
   * Owner's instruction, 2026-09-17: *"the cc list in the signer modal can be a
   * multiselect dropdown options"*.
   *
   * ⚠️ **THE PICKER IS AN ADDITION, NOT A REPLACEMENT, AND THAT IS THE OWNER'S
   * DECISION RATHER THAN CAUTION.** A typed address still works, because the
   * dropdown cannot serve a third of the estate: **291 of 418 companies have a
   * contact carrying an email address** — a reading handed to this change on
   * 2026-09-17 and NOT re-taken here, so re-measure rather than quoting it — so
   * on nearly one company in three a pure dropdown offers nothing at all. The
   * person being copied is routinely somebody who is not on file anyway (a
   * colleague in finance, an agent, a parent institution). Removing the box
   * would take a working path away to add a convenient one.
   *
   * ⚠️ **The ratio is what dates, not the decision.** Even at 418 of 418 the
   * typed box stays: a copy recipient who is not a contact is an ordinary case,
   * not a consequence of the directory being thin.
   *
   * ── WHY THIS READ IS NOT `SignerField`'S ─────────────────────────────────
   *
   * ⚠️ **It cannot share `SignerField`'s fetch and must not try.** That
   * component fetches PER SIGNER ROW, keyed on its own search term, and reports
   * only the chosen `{contactId, name, email}` upward — there is no list to
   * borrow, and reaching into four sibling rows for one would make this control
   * depend on how many signers happen to be on screen. One control, one read.
   *
   * ⚠️ **No search term, deliberately.** `MultiCombobox` has no `onSearch`
   * anywhere in this app: it filters LOCALLY over already-loaded `options`. A
   * term here would narrow the loaded page instead, so the filter box would
   * silently start hiding people it had already been given.
   *
   * ⚠️ **`perPage: 100` is `/contacts`' own ceiling** — it is in the "entities"
   * page-size family, not the masters' 200 — so 101 is a 422 rather than a
   * bigger page. A company with more than 100 contacts shows a prefix, and the
   * typed box below is what reaches the rest.
   */
  const [copyContacts, setCopyContacts] = useState<CopyContacts | null>(null);
  const [copyContactsLoading, setCopyContactsLoading] = useState(false);

  useEffect(() => {
    if (companyId === null) {
      setCopyContacts(null);
      setCopyContactsLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    setCopyContactsLoading(true);

    api
      .listContacts({ companyId, perPage: 100, sort: 'name', order: 'asc' }, signal)
      .then((list) => {
        if (signal.aborted) return;
        /*
         * ⚠️ **A contact with no address is dropped rather than offered.** An
         * option whose only outcome is a refusal is the affordance this
         * codebase removes — and `ccEmails` carries ADDRESSES, so there is
         * literally nothing to put on the wire for such a person.
         *
         * ⚠️ **It is NOT counted and named the way `SignerField` once did.**
         * `contactsWithoutEmailNote` exists and has other callers, but its two
         * render sites on the sibling control were removed under the owner's
         * helper-text ruling of 2026-09-05 and have not come back. Adding the
         * sentence here alone would put an explanation beside the OPTIONAL
         * picker that the REQUIRED one above it does not have.
         */
        const options: CopyContactOption[] = [];
        for (const row of list.data) {
          const email = row.email?.trim() ?? '';
          if (email === '') continue;
          options.push({ id: row.id, name: row.name, email });
        }
        setCopyContacts({ companyId, options, failed: false });
      })
      .catch(() => {
        /*
         * ⚠️ **`failed`, and the control stays fully usable.** A read that did
         * not come back is not evidence that nobody can be copied in: the typed
         * address below is untouched by this, and the picker says it could not
         * load rather than showing an empty list that reads as an empty company.
         */
        if (!signal.aborted) setCopyContacts({ companyId, options: [], failed: true });
      })
      .finally(() => {
        if (!signal.aborted) setCopyContactsLoading(false);
      });

    return () => controller.abort();
  }, [companyId]);

  /** Rows only count while they answer for the company currently on the quote. */
  const copyAnswered =
    copyContacts !== null && copyContacts.companyId === companyId ? copyContacts : null;

  /**
   * Every signer's address, case-folded.
   *
   * Folded because `A@x.com` and `a@x.com` are one mailbox to every mail server
   * involved, and DocuSign — which tells recipients apart by name and address —
   * would meet them as one recipient. The same folding rule the signer duplicate
   * check in `validate` uses, for the same reason.
   */
  const signerEmails = useMemo(
    () =>
      new Set(
        values.signers.map((signer) => signer.email.trim().toLowerCase()).filter(Boolean),
      ),
    [values.signers],
  );

  /**
   * What the picker offers.
   *
   * 🔴 **THE OPTION'S `value` IS THE EMAIL ADDRESS, NOT THE CONTACT ID, AND
   * THAT IS THE WHOLE REASON THIS CONTROL NEEDED NO NEW FORM VALUE.**
   * `ccEmails` is a flat `string[]` of addresses — the wire shape, unchanged —
   * so `MultiCombobox`'s `values` **is** `values.ccEmails`, with no mapping
   * layer to hold a second copy of the selection and no id that has to be
   * resolved back to an address before sending. A typed address is simply a
   * value with no matching option, which the control already renders as a chip
   * labelled with the raw value, so it can always be read and removed.
   * ⛔ Do not "improve" this to ids: the payload would then depend on a lookup
   * that a failed contacts read makes impossible.
   *
   * 🔴 **A SIGNER IS NOT OFFERED — owner's decision, and it makes the clash
   * UNREACHABLE HERE rather than refused.** DocuSign sends every signer the
   * completed document anyway, so copying them adds nothing; that sentence is
   * `SIGNER_CANNOT_BE_COPIED`, which is still live and still needed — see
   * `validate`, where the two paths this exclusion cannot close are named.
   *
   * ⚠️ **De-duplicated by folded address.** Two contacts at one company can
   * legitimately share a mailbox (`info@`), and two options with the same
   * `value` would draw two rows that tick and untick together — which reads as
   * the picker mis-selecting. The first, in the server's `name asc` order,
   * wins.
   */
  const copyOptions = useMemo<MultiComboboxOption[]>(() => {
    const list: MultiComboboxOption[] = [];
    const seen = new Set<string>();
    for (const row of copyAnswered?.options ?? []) {
      const folded = row.email.toLowerCase();
      if (signerEmails.has(folded) || seen.has(folded)) continue;
      seen.add(folded);
      // The address is the `hint` as well as the value: it is what DocuSign is
      // given, and it is what tells two people with one name apart.
      list.push({ value: row.email, label: row.name, hint: row.email });
    }
    return list;
  }, [copyAnswered, signerEmails]);

  /**
   * The address being typed, before it is added. **Not a form value**: it is a
   * draft that has not been committed to anything, and nothing sends it.
   *
   * ⚠️ **Kept OUT of `values.ccEmails` until Add is pressed, which is what
   * retires the blank-row case entirely** — see `validate` and
   * `buildDocumentPayload`, where the rules that existed to tolerate a blank are
   * removed rather than left standing over a state that can no longer occur.
   */
  const [ccDraft, setCcDraft] = useState('');

  /*
   * ⚠️ **`addTypedCopy` is declared BELOW `patch`, not here beside its state.**
   * It is the one piece of this section that writes a form value, and it must
   * go through `patch` — which also clears the server errors and the form
   * messages, so a refused send does not leave a stale banner over an edited
   * list. Declaring it here would put a call above the declaration it depends
   * on; hoisting makes that work and makes it a thing to argue about, which is
   * a poor trade for four lines of adjacency.
   */

  /**
   * Every refused copy, paired with the address it is about.
   *
   * 🔴 **THIS IS WHERE A SERVER `ccEmails.2` NOW LANDS, AND IT IS NO LONGER A
   * ROW — IT IS A CHIP.** The index is still the server's own, built by
   * `ccErrorKey`, and it still indexes `values.ccEmails`; what changed is that
   * there is one control rather than N `Field`s, so the refusal is carried by
   * `MultiCombobox`'s `invalidValues` — which marks the offending chip with
   * tone, a dashed edge, an alert glyph and a screen-reader marker — plus the
   * sentences rendered beneath, each NAMING ITS ADDRESS.
   *
   * ⚠️ **Naming the address is required, not decoration.** A chip is marked but
   * cannot carry prose, so an unattributed list of sentences under a control
   * holding five chips says which rule was broken and not by whom. `Copy 3` used
   * to do that job; there is no "copy 3" on screen any more.
   *
   * ⚠️ **Keyed by INDEX, not by address.** `DUPLICATE_CC_EMAIL` is precisely the
   * case where two entries carry the same string, so an address key would
   * collide on the one refusal that guarantees a collision.
   */
  const ccRefusals = values.ccEmails
    .map((email, index) => ({ index, email, message: errors[ccErrorKey(index)] }))
    .filter(
      (refusal): refusal is { index: number; email: string; message: string } =>
        typeof refusal.message === 'string' && refusal.message !== '',
    );

  /*
   * ⚠️ **`invalid` is NOT passed alongside this.** `MultiCombobox` treats a
   * non-empty `invalidValues` as implying `invalid` for its own border and
   * `aria-invalid`, so passing both would be two sources for one state.
   * `errors.ccEmails` — a refusal about the LIST rather than an entry — is what
   * the control's own `Field` error carries.
   */
  const invalidCcValues = ccRefusals.map((refusal) => refusal.email);

  /**
   * What the empty trigger says, and it is the control's own text rather than a
   * sentence beside it.
   *
   * ⚠️ **The failed case exists because an empty list and an unread one are the
   * same picture.** `SignerField` sets the same precedent one section up
   * (`'No company on this quote'`), which is the only device left for this after
   * the 2026-09-05 helper-text sweep took the sibling control's explanatory
   * lines. It never claims the company has nobody: that is a statement this
   * screen cannot make honestly and, on this control, one it does not need to —
   * the typed address is right below.
   */
  const copyPlaceholder =
    companyId === null
      ? 'No company on this quote'
      : copyAnswered?.failed
        ? 'Contacts could not be loaded'
        : 'Choose a contact…';

  /*
   * ⛔ REMOVED 2026-08-31 with the two toggles: `templatesRequest`, its abort
   * effect, `loadTemplates`, `choosePath`, `chooseDocumentSource` and
   * `selectedTemplate`. Every one of them existed to service a control that is
   * gone; a handler with no control to fire it is a wrong answer waiting for
   * somebody to look something up.
   *
   * ⚠️ **`fetchRefusal` LOST A CLEARING SITE, and the rule it followed still
   * holds.** *SUPERSEDED, from `chooseDocumentSource`: "It goes when a NEW
   * attempt is made: a deliberate switch back to the fetch, a path change, or
   * the next send."* The first two no longer exist, so **the next send is the
   * only clear** — `send()` still does it on entry, which is the one that
   * mattered. It is deliberately NOT cleared in `patch` or `chooseFile`: it
   * reports a fact about the quote (the server could not find it in Xero, or
   * Xero no longer holds the document) and typing a subject line does not make
   * that fact untrue. Clearing it on a keystroke would delete the only sentence
   * explaining why a file picker has appeared, leaving somebody uploading a PDF
   * for no visible reason.
   */

  function patch(next: Partial<FormValues>) {
    setValues((current) => ({ ...current, ...next }));
    setServerErrors({});
    setFormMessages([]);
    setAnchorRefusal(null);
  }

  /**
   * Commit the typed copy address — the second of the two ways into
   * `values.ccEmails`, the picker being the first.
   *
   * ⚠️ **It CANONICALISES to an offered option's own spelling when one matches
   * case-insensitively.** Typing `Ann@x.com` for a contact recorded as
   * `ann@x.com` would otherwise sit as an unmatched chip beside an option row
   * still drawn as unticked — two representations of one person, one of which
   * then trips the duplicate check. The server folds case on this field anyway,
   * so nothing on the wire changes; what changes is that the chip carries the
   * contact's NAME and the row reads as chosen.
   *
   * ⚠️ **It trims, and refuses an empty draft.** That is the whole of what
   * retires the blank entry — see `FormValues.ccEmails`, `validate` and
   * `buildDocumentPayload`, where the three rules that existed to tolerate one
   * are removed rather than left standing over a state nothing can produce.
   *
   * ⚠️ **It does not validate anything else.** `validate()` owns the address
   * rules for every entry however it arrived, and a second copy here would be a
   * second set of sentences to keep in step — including the two,
   * `SIGNER_CANNOT_BE_COPIED` and `DUPLICATE_CC_EMAIL`, that a typed address
   * can still reach and the picker's signer exclusion cannot close.
   *
   * ⚠️ **The cap is re-tested here as well as hidden in the render.** The Add
   * control is absent at `MAX_CCS`, but Enter fires this from a box that can
   * still hold focus for the render in which the cap was reached.
   */
  function addTypedCopy() {
    const typed = ccDraft.trim();
    if (typed === '') return;
    if (values.ccEmails.length >= MAX_CCS) return;
    const folded = typed.toLowerCase();
    const canonical = copyOptions.find((option) => option.value.toLowerCase() === folded);
    patch({ ccEmails: [...values.ccEmails, canonical ? canonical.value : typed] });
    setCcDraft('');
  }

  /* ── the signer list ───────────────────────────────────────────────── */

  /** Replace one signer, leaving the rest — and the delivery rule — alone. */
  function patchSigner(index: number, next: SignerValue) {
    setValues((current) =>
      withSigners(
        current,
        current.signers.map((signer, at) => (at === index ? next : signer)),
      ),
    );
    setServerErrors({});
    setFormMessages([]);
    setAnchorRefusal(null);
  }

  function addSigner() {
    if (values.signers.length >= MAX_SIGNERS) return;
    /*
     * The key is taken OUTSIDE the updater: React may run an updater more than
     * once, and a ref bumped inside one would hand out a key nothing holds.
     */
    const key = nextRowKey.current;
    nextRowKey.current += 1;
    setCountNotice(null);
    setValues((current) => withSigners(current, [...current.signers, EMPTY_SIGNER]));
    setRowKeys((current) => [...current, key]);
    setServerErrors({});
    setFormMessages([]);
  }

  /**
   * Take one signer off, by their own Remove control.
   *
   * ⚠️ **This is the ONLY way a filled-in signer leaves the form**, which is what
   * makes the count control safe — see {@link setSignerCount}. The last signer
   * cannot be removed: an envelope with no recipient is refused upstream, and
   * the form would render with nothing to type into.
   */
  function removeSigner(index: number) {
    if (values.signers.length <= 1) return;
    setCountNotice(null);
    setValues((current) =>
      withSigners(
        current,
        current.signers.filter((_, at) => at !== index),
      ),
    );
    setRowKeys((current) => current.filter((_, at) => at !== index));
    /*
     * The server's per-row errors are keyed by INDEX, so every row after this
     * one has just been renumbered and its message would now be marked against
     * the wrong person. Cleared wholesale rather than re-indexed: a refusal is
     * about the payload that was sent, and this is no longer that payload.
     */
    setServerErrors({});
    setFormMessages([]);
    setClientErrors({});
  }

  /**
   * The "Signers" picker.
   *
   * 🔴 **IT NEVER DISCARDS A SIGNER SOMEBODY HAS FILLED IN.** Lowering the count
   * drops trailing BLANK rows and stops at the first one that has anything in it,
   * saying which row stopped it and where the Remove control is. The picker then
   * shows the real count, because its value is read off the list rather than
   * held separately — so the number visibly does not move, rather than the form
   * silently disagreeing with it.
   *
   * The alternative — dropping whatever the count says, with a confirmation —
   * puts a second destructive gesture on a screen whose Send button already has
   * one, and makes the ordinary case (adding a row, changing your mind) cost a
   * dialog. Removal stays exactly one act, on the row it removes.
   */
  function setSignerCount(requested: number) {
    const target = Math.min(Math.max(requested, 1), MAX_SIGNERS);
    setServerErrors({});
    setFormMessages([]);

    /*
     * Read off `values` rather than through an updater, deliberately: the notice
     * below is decided by the same comparison, and putting a `setState` call
     * inside another one's updater makes it a side effect in a function React is
     * free to run more than once.
     */
    const current = values.signers;
    if (target === current.length) {
      setCountNotice(null);
      return;
    }
    if (target > current.length) {
      setCountNotice(null);
      const added = Array.from({ length: target - current.length }, () => EMPTY_SIGNER);
      const addedKeys = added.map(() => {
        const key = nextRowKey.current;
        nextRowKey.current += 1;
        return key;
      });
      setValues((now) => withSigners(now, [...now.signers, ...added]));
      setRowKeys((now) => [...now, ...addedKeys]);
      return;
    }

    const kept = [...current];
    while (kept.length > target && isBlankSigner(kept[kept.length - 1]!)) kept.pop();
    setCountNotice(
      kept.length > target
        ? `Signer ${kept.length} has been filled in, so the count stopped there. ` +
            'Use Remove on that signer to take them off.'
        : null,
    );
    setValues((now) => withSigners(now, kept));
    // Trimming only ever drops from the END, so the surviving keys are a prefix.
    setRowKeys((now) => now.slice(0, kept.length));
  }

  /*
   * ⛔ `chooseTemplate` was REMOVED 2026-08-31 with the template path, along
   * with the role Select it fed.
   *
   * **The hazard it guarded is unreachable rather than unguarded, and the
   * distinction is the reason this comment survives the function.** DocuSign
   * does NOT error on a `roleName` a template does not declare: it creates the
   * envelope with an unmatched role and NO RECIPIENT, reports success, and the
   * envelope sits at `created` for ever with nobody ever asked to sign —
   * everything looking like it worked. That failure needs a `templateId` to be
   * reachable at all, and this form can no longer send one.
   *
   * ⚠️ **It comes straight back if anybody re-adds a template path here.** The
   * rule is: the role is CHOSEN from `template.roles`, never typed, and a
   * template declaring no roles is refused outright.
   */

  async function chooseFile(picked: File | null) {
    setServerErrors({});
    setFormMessages([]);
    setAnchorRefusal(null);
    setFileError(null);
    setFile(picked);
    setDocumentBase64('');
    if (!picked) return;

    if (picked.size === 0) {
      setFileError('That file is empty.');
      return;
    }
    /*
     * Size is checked against the FILE before it is read, not against the
     * base64 afterwards: reading a 40MB PDF into a data URL to then refuse it
     * spends the memory anyway. The base64 length is re-checked below because
     * the two are related by arithmetic, not by guarantee.
     */
    if (picked.size > MAX_PDF_BYTES) {
      setFileError(
        `That PDF is ${approximateKb(picked.size)}. The limit is ${approximateKb(MAX_PDF_BYTES)} — remove any attached pages.`,
      );
      return;
    }

    setReadingFile(true);
    try {
      const encoded = await readAsBase64(picked);
      if (!encoded.startsWith(PDF_MAGIC_BASE64)) {
        setFileError('That is not a PDF. DocuSign is sent the file exactly as it is here.');
        return;
      }
      if (encoded.length > MAX_BASE64_CHARS) {
        setFileError(
          `Encoded, that PDF is ${encoded.length.toLocaleString()} characters and the limit is ${MAX_BASE64_CHARS.toLocaleString()}. Use a smaller file.`,
        );
        return;
      }
      setDocumentBase64(encoded);
      /*
       * ⛔ REMOVED 2026-08-31: `if (!values.documentName.trim()) patch({
       * documentName: picked.name })`. The name is derived per quote now and can
       * never be empty, so that branch could not fire — and DocuSign showing the
       * signer `Quote-71.pdf` rather than whatever the file was called on
       * somebody's desktop is the behaviour that was already shipping.
       */
    } catch {
      setFileError('That file could not be read. Try choosing it again.');
    } finally {
      setReadingFile(false);
    }
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    /*
     * ⚠️ **Every rule runs PER ROW, and the keys are the server's own** —
     * `signers.0.email`, built by `signerErrorKey` so this half and
     * `knownWriteKeys` cannot disagree on the spelling.
     */
    const seenEmails = new Map<string, number>();
    values.signers.forEach((signer, index) => {
      if (!signer.name.trim()) found[signerErrorKey(index, 'name')] = 'Required.';
      /*
       * ⚠️ The address is checked whichever way it arrived. A picked contact's
       * email comes from `d_contacts`, where the column is free text and nothing
       * validates it — so "it came from a contact" is not evidence it is an
       * address, and DocuSign matching on a malformed one fails in a way nobody
       * sees. See `SIGNER_NEEDS_EMAIL`.
       */
      const email = signer.email.trim();
      if (!email) {
        found[signerErrorKey(index, 'email')] = SIGNER_NEEDS_EMAIL;
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        found[signerErrorKey(index, 'email')] = 'That does not look like an email address.';
        return;
      }
      /*
       * ⚠️ **The same address twice is refused, and the SECOND row is the one
       * marked.** DocuSign tells recipients apart by name and address, so two
       * identical recipients is a real upstream failure rather than a duplicate
       * quietly collapsed — see {@link DUPLICATE_SIGNER_EMAIL}.
       *
       * Case-folded, because `A@x.com` and `a@x.com` are one mailbox to every
       * mail server involved and DocuSign would meet them as one recipient. The
       * FIRST occurrence is deliberately left unmarked: it is not the row
       * somebody needs to change, and marking both would say two fields need
       * attention when one choice fixes it.
       */
      const folded = email.toLowerCase();
      if (seenEmails.has(folded)) {
        found[signerErrorKey(index, 'email')] = DUPLICATE_SIGNER_EMAIL;
        return;
      }
      seenEmails.set(folded, index);
    });

    /*
     * THE COPIED ADDRESSES, per row, keyed the server's own way — `ccEmails.0`,
     * built by `ccErrorKey` so this half and `knownWriteKeys` cannot disagree
     * on the spelling.
     *
     * 🔴 **THE BLANK-ENTRY RULE IS GONE BECAUSE THE BLANK ENTRY IS, 2026-09-17.**
     * *SUPERSEDED:* "⚠️ **A BLANK ROW IS NOT AN ERROR, and that is the one rule
     * here that differs from the signers above.** `buildDocumentPayload` drops
     * blanks, so a row somebody opened and thought better of sends nothing.
     * Refusing it would make the Add control a trap: press it, change your
     * mind, and the form will not submit until you find the Remove button."
     *
     * That rule existed because the section appended `''` and left somebody to
     * fill it in. There are two ways into `values.ccEmails` now and **neither
     * can produce an empty string**: the picker only ever hands back option
     * values and values it was given, and the typed box trims and refuses an
     * empty draft before adding. ⛔ **So the skip is removed rather than kept as
     * a guard** — a `return` that can never be reached reads as a live rule, and
     * the next person to meet it would build an Add control that appends a blank
     * on the strength of it. An empty string arriving from somewhere unforeseen
     * now fails the pattern below, visibly, which is the direction this codebase
     * fails in.
     *
     * ⚠️ **AND THE INDICES LINE UP NOW, WHICH THEY DID NOT BEFORE.** The server
     * names `ccEmails.N` against the array it was SENT; with blanks dropped at
     * the payload the two arrays could differ, so a blank at index 0 put the
     * server's `ccEmails.0` on the form's SECOND entry. Nothing errored — the
     * refusal simply marked the wrong address.
     *
     * ⚠️ **`SIGNER_CANNOT_BE_COPIED` IS STILL REACHABLE, and removing it on the
     * strength of the picker excluding signers would be wrong twice over.** The
     * exclusion closes the dropdown, not the form: an address can be TYPED that
     * belongs to a signer, and somebody can copy a contact in FIRST and make
     * that same person a signer afterwards, which leaves a chip the option list
     * no longer contains. Both end here.
     *
     * ⚠️ **The signer overlap is checked against `seenEmails`, which the loop
     * above has already filled** — so it reads the folded addresses of every
     * signer without walking them a second time and without a second folding
     * rule to keep in step. The ordering of these two blocks is therefore
     * load-bearing; do not move this above the signer loop.
     */
    const seenCcs = new Set<string>();
    values.ccEmails.forEach((raw, index) => {
      const email = raw.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        found[ccErrorKey(index)] = 'That does not look like an email address.';
        return;
      }
      const folded = email.toLowerCase();
      if (seenEmails.has(folded)) {
        found[ccErrorKey(index)] = SIGNER_CANNOT_BE_COPIED;
        return;
      }
      if (seenCcs.has(folded)) {
        found[ccErrorKey(index)] = DUPLICATE_CC_EMAIL;
        return;
      }
      seenCcs.add(folded);
    });

    /*
     * ⛔ The `path === 'template'` branch was REMOVED 2026-08-31 — it validated
     * `templateId` and `signer.roleName`, neither of which this form can send
     * any more.
     *
     * Only the UPLOAD source has a file to validate. On the fetch neither
     * document key is sent and no control is on screen, so a complaint about
     * either would mark nothing and still be counted in "one field needs
     * attention, highlighted above" — the silent failure this file's
     * `knownWriteKeys` exists to prevent, manufactured locally.
     */
    if (documentSource === 'upload' && !documentBase64) {
      found.documentBase64 = 'Choose the quote PDF to send.';
    }
    /*
     * Only reachable once the override is open — until then the value is
     * `DEFAULT_ANCHOR` and cannot fail this. Kept rather than dropped because
     * it guards the one field somebody can now empty, and the server's own
     * minimum is 2.
     */
    if (anchorOverride && values.anchorString.trim().length < 2) {
      found.anchorString = 'Required — at least two characters of text from the PDF.';
    }
    return found;
  }

  /*
   * ⚠️ *SUPERSEDED 2026-08-31: "One builder per path."* There is one path, so
   * `sharedPayload` now has a single consumer — `buildDocumentPayload`. It is
   * kept as its own function because what it holds is the part with a RULE
   * attached, and inlining it buries the rule:
   *
   * The schema is `.strict()`, so an optional field that is empty is OMITTED
   * rather than sent as `''` — `emailSubject` carries a `min(1)` and would be
   * refused, and an absent `dateAnchorString` means "use the server default"
   * while `''` means nothing the server will accept.
   *
   * ⛔ `buildTemplatePayload` was REMOVED with the path. It built `templateId`
   * and `signer.roleName` for `POST /docusign/envelopes`; nothing below needs
   * anything it produced.
   */
  function sharedPayload() {
    const emailSubject = values.emailSubject.trim();
    const emailBlurb = values.emailBlurb.trim();
    return {
      quoteId: quote.id,
      delivery: values.delivery,
      ...(emailSubject ? { emailSubject } : {}),
      ...(emailBlurb ? { emailBlurb } : {}),
    };
  }

  /*
   * ⚠️ The SIGNER is sent and must stay.
   *
   * The "our row, never the request" rule applies only to `createSigningUrl`,
   * where DocuSign matches the values against the envelope exactly. Nothing
   * derives the signer here: both send paths take `signer.name` and
   * `signer.email` from the request and persist them, and the fetch did not
   * change that.
   *
   * ⚠️ And the two document keys are built as ONE object, never as two
   * independent spreads. Absent-and-absent means fetch; bytes with no name is a
   * 422 on `documentName`. The type makes the bad pair uncompilable, and
   * writing them together is what keeps a later edit from separating them.
   */
  function buildDocumentPayload(): DocuSignDocumentEnvelopeInput {
    const supplied = documentSource === 'upload' ? { documentBase64, documentName } : {};
    const ccEmailsToSend = values.ccEmails.map((email) => email.trim());
    return {
      ...sharedPayload(),
      ...supplied,
      /*
       * ⚠️ **ALWAYS THE ARRAY, one signer included.** The endpoint takes either
       * `signer` or `signers` and **never both — sending both is a 422** — so
       * this form has exactly one shape to build and refusals name one set of
       * keys. `signer` is not built here at all any more.
       *
       * Key by key, never a spread: `SignerValue` carries a `contactId` that
       * neither send schema declares, and both are `.strict()`, so spreading it
       * would be a 422 naming a key the user never typed into.
       */
      signers: values.signers.map((signer) => ({
        name: signer.name.trim(),
        email: signer.email.trim(),
      })),
      /*
       * ⚠️ **THE KEY IS OMITTED WHEN NOTHING IS COPIED, and that half is
       * untouched.** `ccEmails: []` and no key at all mean the same thing to the
       * server, and the absent form is what keeps a send that copies nobody
       * **byte-identical to the request this form made before the feature
       * existed**. The endpoint is `.strict()`; there is no cost to the empty
       * array and no benefit either, and "unchanged" is the cheaper thing to
       * reason about after a deploy.
       *
       * 🔴 **THE `.filter(Boolean)` IS GONE, 2026-09-17.** *SUPERSEDED:* "A row
       * somebody opened and abandoned carries `''`, which the schema refuses as
       * not an address. Validation deliberately lets it pass — see there — so
       * this is where it stops." **There is no such row any more**: the picker
       * hands back only option values, and the typed box refuses an empty draft.
       * A filter that can never remove anything reads as a live rule and, worse,
       * would go on quietly re-indexing the array the server names its refusals
       * against — see `validate`, where that mismatch is written up.
       *
       * ⚠️ **The trim stays, as NORMALISATION rather than as a rule.** Both
       * sources already trim, so it removes nothing today; what it guarantees is
       * that whatever reaches the wire is the same string the duplicate and
       * overlap checks compared.
       *
       * ⛔ NOT lowercased here. The server folds it (`toLowerCase()` on the
       * field), so doing it again would be a second copy of a rule that is not
       * ours — and the one place case matters, the signer-overlap check above,
       * folds locally for its own comparison without changing what is sent.
       */
      ...(ccEmailsToSend.length > 0 ? { ccEmails: ccEmailsToSend } : {}),
      /*
       * ⚠️ **DERIVED, AND STILL SENT.** `anchorString` is required by the write
       * schema (2–120 chars), so removing its input meant this line carries
       * `DEFAULT_ANCHOR` on every send that is not an override. A derived field
       * that stops being sent is a 422 on a control nobody can see.
       */
      anchorString: values.anchorString.trim(),
      /*
       * ⛔ `dateAnchorString` is not built any more. It is optional and the
       * server defaults it to the same value the removed box suggested, and that
       * box was empty by default — so this key was already absent from every
       * send. Nothing on the wire changed.
       */
    };
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    /*
     * This dialog is opened from the quote detail page and may sit inside
     * another form's React tree. React propagates by that tree rather than by
     * the DOM, so an un-stopped submit reaches the form behind it.
     */
    event.stopPropagation();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormMessages([]);
    setAnchorRefusal(null);
    if (Object.keys(found).length > 0) return;

    // Nothing is sent yet. The consequence has to be stated and accepted first.
    setConfirmOpen(true);
  }

  async function send() {
    /*
     * ⚠️ *SUPERSEDED 2026-08-31: `const isTemplate = path === 'template'`, and a
     * ternary over two builders and two `api` methods.* One path, one builder,
     * one endpoint — and no cast, because `buildDocumentPayload` already returns
     * the exact input type the method takes.
     */
    const payload = buildDocumentPayload();
    setSubmitting(true);
    setFormMessages([]);
    setAnchorRefusal(null);
    setFetchRefusal(null);
    try {
      const result = await api.sendDocusignDocumentEnvelope(payload);
      setConfirmOpen(false);
      onSent(result.data);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * Split against the keys this form RENDERS, never against the shape of
         * the path. `unroutable` is everything no control here can carry — the
         * empty-path `.strict()` refusal, and any path naming a key the form
         * sends but does not show (`quoteId`). Rendering those at form level is
         * the only way they are seen at all.
         */
        const { fields, unroutable } = caught.routeFieldErrors(
          knownWriteKeys(
            payload as unknown as Record<string, unknown>,
            notRenderedKeys(anchorOverride),
          ),
        );
        setServerErrors(fields);

        const lines = [...unroutable];
        if (caught.code === QUOTE_NOT_IN_XERO) {
          /*
           * The only one of the three recoverable inside this same request. The
           * server's 422 names `documentBase64` — which, on a fetch, is a key
           * this form deliberately did not send and has no control for, so it
           * arrives in `unroutable` and would otherwise read as a dead end.
           *
           * So the picker is switched ON and the refusal explains it. Never a
           * bare "push it to Xero first": that is a real option and it is
           * offered, but it sends somebody to another screen when the thing
           * that fixes it is now directly underneath the message.
           */
          setDocumentSource('upload');
          setFetchRefusal({ kind: 'not-in-xero', message: caught.message });
          /*
           * Dropped from the banner rather than shown twice. `unroutable` here
           * is the server's own `documentBase64: Required while this quote is
           * not in Xero`, which is the same fact the notice states with the
           * control beside it — and this is a stated decision, not a silent
           * `??`: anything the notice does NOT carry stays in `lines`.
           */
          setFormMessages(lines.filter((line) => !line.includes('documentBase64')));
          setConfirmOpen(false);
          setSubmitting(false);
          return;
        }
        if (caught.code === QUOTE_GONE_FROM_XERO) {
          /*
           * ⚠️ Checked BEFORE the generic `status === 409` branch below, which
           * appends "void the existing one before sending another" — advice
           * about a completely different refusal that happens to share a status
           * code. Left to fall through, it would send somebody hunting an
           * envelope that does not exist.
           */
          setFetchRefusal({ kind: 'gone', message: caught.message });
          setFormMessages(lines);
          setConfirmOpen(false);
          setSubmitting(false);
          return;
        }
        if (isAnchorRefusal(caught)) {
          // Verbatim, and beside the anchor field as well: DocuSign names the
          // string it searched the PDF for, which is the actionable half and
          // is not something this app can reconstruct.
          setAnchorRefusal(caught.message);
          /*
           * ⚠️ The field is REVEALED here, in the same statement that marks it.
           * The derived anchor has just been proven wrong for this PDF, so this
           * is the moment the question becomes answerable — and marking a
           * control that is not on screen is the silent failure
           * `notRenderedKeys` exists to prevent.
           */
          setAnchorOverride(true);
          setServerErrors((current) => ({
            ...current,
            anchorString: 'DocuSign could not find this text in the PDF.',
          }));
        } else if (caught.code === QUOTE_STILL_DRAFT_IN_XERO) {
          /*
           * ⚠️ **ABOVE the generic `status === 409` branch, and for exactly the
           * reason `QUOTE_GONE_FROM_XERO` already states above:** that branch
           * appends "Void the existing envelope before sending another" —
           * "advice about a completely different refusal that happens to share a
           * status code. Left to fall through, it would send somebody hunting an
           * envelope that does not exist."
           *
           * That warning was written about the GONE refusal and this code walked
           * straight into it: nothing was sent, so there is no envelope to void.
           * **A third 409 arriving here goes above that branch too** — the
           * generic one is a fallback for the one-live-envelope refusal alone,
           * and every named 409 has to be lifted out of it by hand.
           *
           * The server's sentence and nothing else. It already leads with
           * "Nothing was sent", which is also the title of the banner it renders
           * in, so the two agree without this file restating either. Deliberately
           * NOT `fetchRefusal`: both of that notice's kinds append a recovery
           * naming the file picker, and the picker does not fix this — see
           * {@link QUOTE_STILL_DRAFT_IN_XERO}.
           */
          lines.push(caught.message);
        } else if (caught.status === 409) {
          lines.push(
            `${caught.message} Void the existing envelope before sending another.`,
          );
        } else if (lines.length === 0 && Object.keys(fields).length === 0) {
          lines.push(caught.message);
        }
        setFormMessages(lines);
      } else {
        setFormMessages(['Could not reach the server. Nothing was sent. Check your connection.']);
      }
      setConfirmOpen(false);
      setSubmitting(false);
    }
  }

  const notBinding = signaturesNotBinding(connection);
  const errorCount = Object.keys(errors).length;
  /*
   * ⚠️ **More than one signer means EMAIL, and this is the one test.** Read off
   * the list rather than kept as its own flag, so it cannot disagree with what
   * is on screen. `withSigners` has already written `delivery: 'email'`; this
   * decides what the control is allowed to offer.
   */
  const multipleSigners = values.signers.length > 1;
  const firstSigner = values.signers[0] ?? EMPTY_SIGNER;
  /*
   * ⚠️ EMERGE's identifier for this quote, and ONLY EMERGE's — never
   * `quoteNumber ?? xeroQuoteNumber ?? #id`. That fallback would silently swap
   * references on every "quote {label}" on this screen — the title, the fact
   * strip, the acceptance notice, the confirmation — with nothing saying so;
   * the two are typeset identically and mean different systems' records.
   * Xero's number is shown only where it is labelled as Xero's, and only once
   * the link row says this quote is in Xero.
   */
  const quoteLabel = quote.quoteNumber ?? `#${quote.id}`;
  const total = `${formatDecimalString(quote.grandTotal)}${quote.currency ? ` ${quote.currency}` : ''}`;

  /*
   * ⚠️ Nothing here disables Send on the Xero LINK STATUS, and that is
   * deliberate — see the header. A `conflict` link means Xero's copy is newer
   * than our baseline, so the document is present and readable; refusing would
   * block signature on exactly the quotes somebody has most recently edited.
   * ⚠️ *SUPERSEDED 2026-08-31: "no file on a source that needs one, and no
   * templates on a path that needs one."* The template clause went with the
   * path. **ONE disabled case is left that is about the document at all**: an
   * upload with no readable file. The fetch is never disabled — there is
   * nothing provable from data in hand that would justify it.
   */
  const sendDisabled =
    submitting ||
    readingFile ||
    (documentSource === 'upload' && (!documentBase64 || Boolean(fileError)));

  /*
   * Preview follows the SOURCE (see "PREVIEW, BEFORE IT GOES"). The Xero viewer
   * needs `quotes.read`; the uploaded file is local bytes and needs nothing, but
   * only once it read cleanly — a file that failed is not what would be sent.
   * Absent rather than disabled when there is nothing to show yet.
   */
  const canPreview =
    documentSource === 'xero'
      ? canPreviewQuotePdf(ability)
      : Boolean(file && documentBase64 && !fileError && !readingFile);
  function openPreview() {
    if (documentSource === 'xero') {
      setPreviewTarget({
        id: quote.id,
        quoteNumber: quote.quoteNumber,
        subject: quote.subject,
        companyName: quote.company ? documentCustomerName(quote.company) : null,
      });
    } else if (file) {
      closeFilePreview();
      // Typed as PDF regardless of the file's own MIME type, so a mislabelled
      // file can never render as same-origin HTML in the unsandboxed frame.
      const url = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }));
      filePreviewUrlRef.current = url;
      setFilePreviewUrl(url);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        dismissOnBackdrop={false}
        title={`Send quote ${quoteLabel} for signature`}
        footer={
          <>
            {errorCount > 0 && (
              <p className="self-center text-xs text-danger sm:mr-auto">
                {errorCount === 1
                  ? 'One field needs attention. It is highlighted above.'
                  : `${errorCount} fields need attention. They are highlighted above.`}
              </p>
            )}
            {canPreview && (
              <Button
                variant="secondary"
                onClick={openPreview}
                disabled={submitting}
                className={errorCount > 0 ? undefined : 'sm:mr-auto'}
              >
                {PREVIEW_PDF_LABEL}
              </Button>
            )}
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" form={formId} variant="primary" disabled={sendDisabled}>
              Review and send
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={onSubmit} noValidate className="grid min-w-0 gap-5">
          {/*
            The sandbox warning sits above everything, including the quote it is
            about. It is the loudest thing on this screen because this is the
            last screen before an envelope leaves, and because a sandbox
            signature is indistinguishable from a real one at every later step.

            ⚠️ **It no longer appears on a connection we could not read** — see
            the header. It is a statement about an environment somebody actually
            reported, not about our own ignorance.
          */}
          {notBinding && (
            <Notice tone="danger" role="alert" title="These signatures are NOT legally binding">
              <p className="m-0">
                DocuSign is on{' '}
                <strong>{docuSignEnvironmentLabel(connection?.environment)}</strong>, so a signature
                from here is worth nothing.
              </p>
            </Notice>
          )}

          {/*
            ⚠️ **REMOVED on the owner's instruction, 2026-08-30.** A standing
            `Notice` sat here titled "A completed signature accepts this quote",
            saying the stage moves on its own with nobody at a screen. It is
            gone from this screen and from `EnvelopePanel`; the owner asked for
            both.

            **What it said is still TRUE, which is why this comment exists
            rather than nothing.** A completed signature still moves the quote
            into the accepted stage, it still asks nobody, and the backend's
            status worker still polls outstanding envelopes on a timer — so it
            can happen with no one on this screen. Only the notice was removed;
            the behaviour was not. `signatureConsequenceNote()` and
            `SIGNATURE_ACCEPTS_QUOTE_NOTE` in `@/lib/docusign` are untouched and
            still carry the wording for any surface that needs it.

            **Do not restore it as an oversight.** Removing it was a decision.
            The sandbox warning above is NOT part of it and must stay.
          */}

          <dl className="grid min-w-0 grid-cols-1 gap-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5 sm:grid-cols-3">
            <Fact label="Quote" value={quoteLabel} />
            <Fact label="Customer" value={quote.company ? documentCustomerName(quote.company) : 'Not recorded'} />
            <Fact label="Total" value={total} />
            {/*
              ⚠️ **THE XERO NUMBER SURVIVES THE NOTICE THAT CARRIED IT.** The
              blue "EMERGE fetches the PDF from Xero for you — in Xero it is
              QU-0490, check it there first" block was removed on the owner's
              instruction (2026-08-31), and it was the only place the number
              appeared on this screen. The EXPLANATION is what he asked to go —
              with no source toggle there is no choice left to explain — but the
              number is what somebody types into Xero's search to look at the
              document before it is signed, so it stays as a plain labelled fact.

              ⚠️ **Labelled "In Xero", never merged into `Quote` above.**
              `quoteNumber` is OURS and `xeroQuoteNumber` is Xero's; they are
              typeset identically, name records in different systems, and this
              file's own header is emphatic that Xero's is shown only where it is
              labelled as Xero's.

              Rendered only when the LINK ROW says the quote is in Xero and a
              number is actually recorded — the number is written best-effort, so
              a quote can be in Xero with none. The "not in Xero yet" warning
              below covers the other case; a fourth fact reading "—" would say
              nothing and imply a gap.
            */}
            {xeroRef.inXero && xeroRef.number && (
              <Fact label="In Xero" value={xeroRef.number} />
            )}
          </dl>

          {/*
            ⛔ **THREE BLOCKS WERE REMOVED HERE ON 2026-08-31, all owner's
            instructions, and each is quoted so a reader meeting the gap knows
            it was a decision rather than an omission.**

            1. *"What gets signed"* — two buttons, `The quote PDF` and `A
               DocuSign template`, with a line reading either "The PDF Xero
               produced, with every figure on it." or "A fixed DocuSign form —
               it cannot show this quote's line items." **"On the send quote,
               'What gets signed' will always be a quote PDF, so remove this
               section, no need to ask."** The whole template path went with it;
               see the header for what it carried and why nothing is owed.

            2. *"Where the PDF comes from"* — two buttons, `Fetch it from Xero`
               and `Upload a PDF`, shown only when the quote was in Xero.
               **"'Where the PDF comes from' — it will always come from Xero, so
               remove this section which gives these two options."** ⚠️ The
               SOURCE still has two values and the file picker still appears on
               the two states that have no alternative — not in Xero, and the
               `DOCUSIGN_QUOTE_NOT_IN_XERO` 422. What went is the choice.

            3. The blue notice titled *"EMERGE fetches the PDF from Xero for
               you"*, reading *"In Xero it is QU-0490 — check it there first."*
               or, with no number recorded, *"Its Xero quote number is not
               recorded here; the fetch follows the link, not the number."*
               **"And also this blue warning."** With no toggle above it, it
               explained a choice nobody makes. ⚠️ **Its Xero NUMBER was the only
               one on this screen and is now a labelled fact in the strip above**
               — the explanation went, the identifier did not.

            The reasoning under (2) and (3) that is still load-bearing, and is
            the reason `xeroDocumentRef` survives intact: **which Xero document
            this is comes from the LINK ROW, never from `xeroQuoteNumber`.** That
            field is written best-effort and has survived an unlink, so it can be
            absent on a quote demonstrably in Xero and has named another quote's
            document. It is printed as a convenience on top of the link row's
            answer, never in place of it, and never with our own `quoteLabel`
            standing in for it.
          */}
          <div className="grid min-w-0 gap-4">
            {xeroRef.inXero && documentSource === 'upload' && (
              <Notice tone="info" role="status" title="Download the PDF from Xero first">
                <p className="m-0">
                  {xeroRef.number ? (
                    <>
                      Open <strong>{xeroRef.number}</strong> in Xero, download the PDF, then choose
                      it below.
                    </>
                  ) : (
                    <>
                      No Xero quote number recorded &mdash; find it in Xero under{' '}
                      {quote.company ? documentCustomerName(quote.company) : 'the customer'} ({total}), then choose it below.
                    </>
                  )}
                </p>
              </Notice>
            )}

            {/*
              ⚠️ **CORRECTED 2026-08-31 — it named a control that no longer
              exists.** *SUPERSEDED: title "This quote is not in Xero, so there
              is nothing to fetch", body "Push it from this quote's Xero
              panel, or choose a PDF below."* The manual push was removed from
              quotes and invoices on the owner's instruction — `XeroPanel`
              withholds Create, Send and Retry on `CREATED_BY_SAVING` — so
              that sentence sent somebody hunting a button that is gone.

              The wording is the panel's own ("This quote goes to Xero when
              you save it"), deliberately: two phrasings for one fact is how
              two screens come to describe auto-push differently.
            */}
            {!xeroRef.inXero && (
              <Notice tone="warning" role="status" title="This quote is not in Xero yet">
                <p className="m-0">
                  It goes to Xero when you save it. Until then, choose a PDF below.
                </p>
              </Notice>
            )}

            {/*
              A refusal about the fetch. Kept separate from the form-level
              "Nothing was sent" banner because it says what to do INSTEAD,
              and in the 422 case the control that does it has just been
              switched on below this message.
            */}
            {fetchRefusal && (
              <Notice
                tone="danger"
                role="alert"
                title={
                  fetchRefusal.kind === 'not-in-xero'
                    ? 'Xero has no document for this quote'
                    : 'The Xero document is gone'
                }
              >
                {/* The server's own sentence, verbatim — it is the half that
                    names what upstream actually said. */}
                <p className="m-0">{fetchRefusal.message}</p>
                {/*
                  ⚠️ **BOTH SENTENCES NAMED THE REMOVED PUSH, and the second
                  one was wrong twice over.** *SUPERSEDED: "Choose a PDF below,
                  or push this quote to Xero first." and "Recreate it from this
                  quote's Xero panel, or upload a PDF."*

                  The first is now the save, like the notice above. The second
                  does NOT say "save it again": this branch is an ARCHIVED link
                  or a Xero 404, and an archived link stops syncing — so a save
                  would report success and send nothing. It points at the panel
                  for the state and names the recovery that actually works.
                */}
                {fetchRefusal.kind === 'not-in-xero' ? (
                  <p className="m-0">
                    Nothing was sent. Choose a PDF below, or save the quote to send it to Xero.
                  </p>
                ) : (
                  <p className="m-0">
                    Nothing was sent. Upload a PDF, or check this quote&rsquo;s Xero panel.
                  </p>
                )}
              </Notice>
            )}

            {/*
              The file picker belongs to the UPLOAD source and is ABSENT on the
              fetch rather than disabled: neither document key is sent there,
              so a control for one would configure nothing.

              ⚠️ *SUPERSEDED 2026-08-31: "Both controls belong to the UPLOAD
              source".* There is ONE now — the `documentName` box beside it is
              derived. The reasoning it carried is worth keeping and is why
              the derived name is only ever STATED on this source: a name box
              that appears to name the FETCHED document, which the server names
              from the Xero quote number, would be the "looks like it
              configures the accounting system and silently does not" shape
              this repo refuses elsewhere.

              🔴 **THIS IS NOW THE ONLY THING THAT PUTS THE UPLOAD SOURCE ON
              SCREEN, and it is reached without anybody choosing it** — the
              "Where the PDF comes from" toggle above was removed on
              2026-08-31. It renders on exactly two states, both of which have
              no alternative: a quote that is not in Xero, and the
              `DOCUSIGN_QUOTE_NOT_IN_XERO` 422, which `send()` recovers from by
              flipping the source here. **Gating it on anything narrower — a
              literal `'xero'`, or deleting the branch as dead — turns that
              refusal into a dead end.**
            */}
            {documentSource === 'upload' && (
              <>
                {/*
                  A DELIBERATE precedence, not a silent drop — this repo's
                  `a ?? b` rule asks for the decision to be stated. Both sides
                  can be set at once: a file that failed to read leaves
                  `documentBase64` empty, so `validate()` also adds the generic
                  "Choose the quote PDF to send." `fileError` wins because it
                  is the SPECIFIC reason for the generic one — it names the
                  file and its size — and nothing is lost, because the generic
                  message adds no information the specific one has not already
                  given. The remaining occupant of that key is a server 422 on
                  `documentBase64`, which cannot coexist: `chooseFile` clears
                  `fileError` and the server errors together, and
                  `sendDisabled` refuses to send while either a file error or
                  an empty `documentBase64` stands.

                  ⚠️ The FETCH's 422 also names `documentBase64` and never
                  reaches this key: on that source the key is not in the
                  payload, so `knownWriteKeys` does not offer it and
                  `routeFieldErrors` cannot mark it. It is caught by code
                  first and rendered as the refusal notice above.
                */}
                <Field label="Quote PDF" required error={fileError ?? errors.documentBase64}>
                  {(fieldProps) => (
                    <div className="grid gap-1.5">
                      <input
                        {...fieldProps}
                        type="file"
                        accept="application/pdf,.pdf"
                        disabled={submitting}
                        onChange={(event) => void chooseFile(event.target.files?.[0] ?? null)}
                        className="block w-full min-w-0 cursor-pointer rounded-md border border-border bg-surface text-sm text-fg file:mr-3 file:cursor-pointer file:rounded-l-md file:border-0 file:bg-surface-sunken file:px-3 file:py-2 file:text-sm file:text-fg-secondary hover:border-hairline-strong"
                      />
                      {readingFile && <Skeleton className="h-4 w-40" />}
                      {/*
                        The derived document name is STATED here rather than
                        asked for. It is what the signer sees in DocuSign and
                        keeps afterwards, and it is not the file's own name —
                        a rename nobody is told about is a surprise, where one
                        clause is not a field.
                      */}
                      {!readingFile && file && documentBase64 && (
                        <p className="m-0 text-xs text-fg-muted">
                          {file.name} &middot; {approximateKb(file.size)}, sent as {documentName}.
                        </p>
                      )}
                    </div>
                  )}
                </Field>
              </>
            )}

            {/*
              ⚠️ **THREE FIELDS STOOD HERE AND ALL THREE ARE DERIVED NOW —
              owner's instruction, 2026-08-31.** *SUPERSEDED: a required
              "Document name" pre-filled with the value it is still sent as, a
              required "Signature anchor text" pre-filled with `Signature:`,
              and an optional "Date anchor text" whose hint named the server
              default it was already leaving to the server.*

              Every one of them asked a question this form could answer
              itself, and the anchors asked it of somebody who cannot see the
              PDF's text layer: **a wrong anchor is a send that never happens,
              and the only sign of it is DocuSign's refusal.** Deriving them
              does not make that refusal unreachable — an uploaded PDF need
              not carry Xero's Terms block at all — so the override below
              appears at the moment DocuSign says the derived text is not
              there, which is the first moment a person can answer better than
              the constant can.

              ⚠️ **UNTOUCHED by the two toggle removals of 2026-08-31.** The
              anchor recovery keys on `ANCHOR_TAB_STRING_NOT_FOUND` and reveals
              the field below; it never had anything to do with the source
              buttons, and a fetched Xero PDF can refuse an anchor exactly as an
              uploaded one can.
            */}
            {anchorRefusal && (
              <Notice tone="danger" role="alert" title="DocuSign refused the anchor text">
                <p className="m-0">{anchorRefusal}</p>
                <p className="m-0">
                  Nothing was sent. Copy the exact text from the PDF into the box below.
                </p>
              </Notice>
            )}

            {anchorOverride && (
              <Field
                label="Signature anchor text"
                required
                error={errors.anchorString}
              >
                {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    value={values.anchorString}
                    maxLength={120}
                    placeholder={DEFAULT_ANCHOR}
                    invalid={Boolean(errors.anchorString)}
                    disabled={submitting}
                    onChange={(event) => patch({ anchorString: event.target.value })}
                  />
                )}
              </Field>
            )}
          </div>

          {/* ── who signs ──────────────────────────────────────────── */}
          {/*
            ⚠️ **Two free-text boxes became a CONTACT PICKER — owner's
            instruction, 2026-08-31.** The picker is scoped to this quote's own
            company and seeds that company's primary contact, through
            `@/lib/contact-scope` — the one module `DealFormModal`,
            `QuoteFormModal` and `InvoiceFormModal` already share for the same
            decision. `SignerField` owns the whole of it, including the two cases
            typing never had: a contact with no email address, and a signer who
            is not a contact at all.

            ⚠️ **CORRECTED 2026-09-01 — the second half of this stopped being
            true.** *SUPERSEDED: "**The WIRE IS UNCHANGED.** `signer.name` and
            `signer.email` are still two strings and there is still no contact id
            in either send schema. The field is a better way to fill them, not a
            new shape."*

            The clause it was written to make — **the picker changed how the two
            strings get filled in, not what is sent** — still holds, and there is
            still no contact id in either send schema. But the shape around them
            moved: this form now sends `signers`, an ARRAY of those same pairs,
            and one signer is an array of one. See the multiple-signers block
            below.

            ⛔ The `hint` that used to hang on the email box — *"DocuSign matches
            on this exactly."* — was REMOVED on the same instruction. It is moot
            once the address comes from a picked contact; the half of it that is
            still load-bearing lives in `SIGNER_NEEDS_EMAIL`, where the picker
            uses it to explain who cannot be offered.
          */}
          {/*
            ⚠️ **UP TO FOUR SIGNERS — owner's decision, 2026-09-01**, in his
            words: *"in some cases or in some companies there can be multiple
            signers … there should be a picker for the number of signers near the
            signer and an add signer button."* Four rulings came with it and none
            is open: at most four; **all of them receive it at once** with no
            ordering control; **more than one is email delivery only**; and each
            signer is independently a contact or a hand-typed person, which is
            why every row is its own `SignerField` with its own picker and its
            own mode.

            **One signer renders exactly as it did before this existed** — no
            ordinal, no Remove, no row chrome — so the ordinary send is
            untouched.
          */}
          <div className="grid min-w-0 gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Signers">
                {(fieldProps) => (
                  <div className="w-28">
                    <Select
                      {...fieldProps}
                      value={String(values.signers.length)}
                      disabled={submitting}
                      onChange={(event) => setSignerCount(Number(event.target.value))}
                    >
                      {Array.from({ length: MAX_SIGNERS }, (_, index) => index + 1).map(
                        (count) => (
                          <option key={count} value={count}>
                            {count}
                          </option>
                        ),
                      )}
                    </Select>
                  </div>
                )}
              </Field>
              {/*
                ⚠️ **ABSENT at the ceiling rather than disabled with a sentence.**
                The picker beside it stops at four, so the cap is already on
                screen; a dead button plus a line of prose explaining a limit the
                neighbouring control states is the helper text this repo removes.
              */}
              {values.signers.length < MAX_SIGNERS && (
                <Button type="button" size="sm" disabled={submitting} onClick={addSigner}>
                  Add signer
                </Button>
              )}
            </div>

            {/*
              Why the picker did not go where it was pushed. Its own line, not a
              field error: the form is valid, an action was declined — see
              `setSignerCount`.
            */}
            {countNotice && (
              <p className="m-0 text-xs text-warning [overflow-wrap:anywhere]">{countNotice}</p>
            )}

            {/*
              ⚠️ A 422 naming `signers` itself — the count, the shape — has no
              control to bind to, so it is rendered here rather than left to mark
              nothing while still counting towards "one field needs attention".
              `knownWriteKeys` offers the key, so this is the control it offers it
              for.
            */}
            {errors.signers && (
              <p className="m-0 text-xs text-danger [overflow-wrap:anywhere]">{errors.signers}</p>
            )}

            {values.signers.map((signer, index) => {
              /*
               * ⚠️ **NOT keyed by index — see `rowKeys`.** A `SignerField` holds
               * state the value does not carry (its mode, its search, its loaded
               * contacts), and React keeps that with the key; keyed by position,
               * removing a row slides one signer's value into another's field.
               *
               * The per-row error IS keyed by index, because that is how the
               * server names it — and it is exactly why `removeSigner` throws the
               * errors away rather than re-indexing them.
               */
              const rowError = errors[`signers.${index}`];
              return (
                <div
                  key={rowKeys[index] ?? index}
                  className={
                    multipleSigners
                      ? 'grid min-w-0 gap-2 rounded-md border border-border px-3 py-3'
                      : 'grid min-w-0 gap-2'
                  }
                >
                  <SignerField
                    /*
                     * ⚠️ **The shared derivation, not a second `quote.company?.id
                     * ?? null` written out here.** The copy picker below is
                     * scoped to the same company, and two spellings of one
                     * scope is how the two controls come to offer contacts from
                     * different accounts.
                     */
                    companyId={companyId}
                    value={signer}
                    onChange={(next) => patchSigner(index, next)}
                    errors={{
                      name: errors[signerErrorKey(index, 'name')],
                      email: errors[signerErrorKey(index, 'email')],
                    }}
                    disabled={submitting}
                    {...(multipleSigners ? { ordinal: index + 1 } : {})}
                    /*
                     * 🔴 Only the row this form OPENED with seeds the company's
                     * primary contact — see {@link FIRST_ROW_KEY}. The seed is
                     * per-field, so every row seeding would fill four rows with
                     * the same person, and an envelope naming one person four
                     * times is refused by DocuSign, which tells recipients apart
                     * by name and email. It would look like the form working.
                     */
                    seedPrimaryContact={rowKeys[index] === FIRST_ROW_KEY}
                    action={
                      multipleSigners ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={submitting}
                          onClick={() => removeSigner(index)}
                        >
                          Remove
                        </Button>
                      ) : undefined
                    }
                  />
                  {rowError && (
                    <p className="m-0 text-xs text-danger [overflow-wrap:anywhere]">{rowError}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/*
            ── WHO ELSE GETS IT ────────────────────────────────────────────
            Owner instruction, 2026-09-16.

            ⚠️ **BELOW THE SIGNERS AND ABOVE DELIVERY, and the position is a
            claim.** Reading down, the form now answers "who signs", then "who
            else hears about it", then "how it reaches them" — and the copy
            rows are unaffected by the delivery fork, so putting them after it
            would invite the reading that "I deliver the link myself" holds the
            copies back too. It does not: they are emailed by DocuSign either
            way.

            ⚠️ *SUPERSEDED 2026-09-17, on the owner's instruction that this be
            "a multiselect dropdown options":* **"NO ROWS UNTIL SOMEBODY ASKS
            FOR ONE.** The section opens as a heading and an Add control, unlike
            the signer list above which always shows one row. A signer is
            required; a copy is not, and an empty address box on every send
            implies an answer is expected." **The REASON survives and the shape
            it argued for did not.** One empty picker is not an empty address
            box: it asks nothing, it shows a placeholder rather than a required
            field, and it holds every chosen person at once — so the section can
            open with its control on screen without implying an answer is
            expected. What must not come back is a row per copy.
          */}
          <div className="grid min-w-0 gap-3">
            <h3 className="m-0 text-sm font-medium text-fg">Copy in</h3>

            {/*
              ⛔ **NOT helper text — see `CC_EXPLAINER`, where the test is
              applied.** It names a consequence the control cannot: an email
              leaves for a real person the moment Send is pressed. A sweep must
              not take it, and that licence covers this sentence alone.

              ⚠️ **Still gated on there BEING a copy**, which the picker did not
              change: with nothing chosen, no email leaves and the sentence
              describes nothing.
            */}
            {values.ccEmails.length > 0 && (
              <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">{CC_EXPLAINER}</p>
            )}

            {/*
              ⚠️ **`hideLabel`, because the `h3` above already says it.** Two
              visible "Copy in"s one above the other is the same fact twice;
              dropping the label outright would leave an unnamed control for
              assistive tech — the rule `SignerField` states for its own
              ordinal rows.

              ⚠️ **`errors.ccEmails` is the LIST-level refusal** — the count,
              the shape — and it belongs on the control rather than as a loose
              line, now that there is one control for it to mark. A refusal
              about a single ENTRY is rendered under it instead: see
              `ccRefusals`, where that routing is written out.
            */}
            <Field label="Copy in" hideLabel error={errors.ccEmails}>
              {(fieldProps) => (
                <MultiCombobox
                  {...fieldProps}
                  values={values.ccEmails}
                  /*
                   * ⚠️ **The control's values ARE the form's addresses.** No
                   * mapping, because an option's `value` is the address itself
                   * — see `copyOptions`. Removing a chip and unticking a row
                   * are therefore one operation, and a typed address is
                   * removable in the same place as a chosen contact.
                   */
                  onChange={(next) => patch({ ccEmails: next })}
                  options={copyOptions}
                  loading={copyContactsLoading}
                  /*
                   * ⚠️ **NOT disabled when the quote names no company**, which
                   * is where this parts company with `SignerField`'s picker.
                   * Chips can exist with no company — every one of them typed
                   * by hand — and disabling the control would strand them with
                   * no way to take them off.
                   */
                  disabled={submitting}
                  maxSelected={MAX_CCS}
                  invalidValues={invalidCcValues}
                  placeholder={copyPlaceholder}
                  searchPlaceholder="Search this company’s contacts…"
                  /*
                   * ⛔ **NO `selectAll`, and it is a decision rather than an
                   * omission.** Three independent reasons, any one of which is
                   * enough: this is an EDIT field whose stored set makes an
                   * email leave for every person in it, so "tick everybody at
                   * this company" is the one gesture here that should cost more
                   * than one press; the list is capped at `/contacts`' own 100
                   * and can therefore be short, which is exactly the truncation
                   * interlock `MultiCombobox` documents; and
                   * `'even-when-truncated'` — the only way past that interlock
                   * — is stated there as FILTERS ONLY, which this is not.
                   */
                />
              )}
            </Field>

            {/*
              Each refused address, named. The chip is marked by
              `invalidValues`; a marked chip cannot carry prose, so the sentence
              lives here and says WHOSE it is — `SIGNER_CANNOT_BE_COPIED` and
              `DUPLICATE_CC_EMAIL` are both about a relationship between two
              entries, and neither is actionable without knowing which.
            */}
            {ccRefusals.map((refusal) => (
              <p
                key={refusal.index}
                className="m-0 text-xs text-danger [overflow-wrap:anywhere]"
              >
                <strong>{refusal.email}</strong> — {refusal.message}
              </p>
            ))}

            {/*
              ── AND SOMEBODY WHO IS NOT ON FILE ─────────────────────────────

              🔴 **THE TYPED PATH IS NOT A FALLBACK BEHIND A FAILURE — it is the
              only path that works at all on nearly a third of companies**, and
              it is the one the picker was added ALONGSIDE rather than in place
              of. See the contacts read above for the figure and for how old it
              is. It is also how a colleague in finance, an agent or somebody at
              a parent institution is copied — people a CRM contact record was
              never going to hold.

              ⚠️ **ABSENT at the ceiling rather than disabled**, the rule this
              section has always stated: a dead control plus a line explaining a
              limit is the helper text this repo removes. The picker above says
              why at the cap through `maxSelected`, which is the component's own
              wording rather than a second sentence here.
            */}
            {values.ccEmails.length < MAX_CCS && (
              <div className="flex min-w-0 items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Field label="Someone else">
                    {(fieldProps) => (
                      <Input
                        {...fieldProps}
                        type="email"
                        value={ccDraft}
                        maxLength={255}
                        autoComplete="off"
                        disabled={submitting}
                        onChange={(event) => setCcDraft(event.target.value)}
                        /*
                         * 🔴 **ENTER IS CAUGHT, AND WITHOUT THIS IT SENDS THE
                         * QUOTE.** This input sits inside the dialog's form, so
                         * an un-prevented Enter submits it and opens the
                         * confirmation — from a box whose whole purpose is that
                         * pressing Enter adds a row. The address being typed
                         * would be discarded on the way, so somebody would be
                         * one more press from sending an envelope missing the
                         * person they were in the middle of copying in.
                         */
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          addTypedCopy();
                        }}
                      />
                    )}
                  </Field>
                </div>
                {/*
                  ⚠️ **Disabled on an EMPTY draft, which is what retires the
                  blank entry.** Nothing can reach `values.ccEmails` without
                  passing here or through the picker, and neither can produce an
                  empty string — see `validate`, where the rule that used to
                  tolerate one is removed rather than left standing.
                */}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="mt-6 shrink-0"
                  disabled={submitting || ccDraft.trim() === ''}
                  onClick={addTypedCopy}
                >
                  Add
                </Button>
              </div>
            )}
          </div>

          <DeliveryChoice
            name={deliveryName}
            value={values.delivery}
            onChange={(delivery) => patch({ delivery })}
            disabled={submitting}
            lockedToEmail={multipleSigners}
          />

          <div className="grid min-w-0 gap-4">
            <Field label="Subject" error={errors.emailSubject}>
              {(fieldProps) => (
                <Input
                  {...fieldProps}
                  value={values.emailSubject}
                  maxLength={255}
                  invalid={Boolean(errors.emailSubject)}
                  disabled={submitting}
                  /*
                    ⚠️ **The flag is set on EVERY change, the one that empties
                    the box included** — see the seeding block. Setting it only
                    for non-empty text would hand a cleared field back to the
                    seeder, which refills it, and the box fights the person
                    trying to empty it.
                  */
                  onChange={(event) => {
                    subjectTouched.current = true;
                    patch({ emailSubject: event.target.value });
                  }}
                />
              )}
            </Field>
            <Field label="Message to the signer" error={errors.emailBlurb}>
              {(fieldProps) => (
                <Textarea
                  {...fieldProps}
                  value={values.emailBlurb}
                  maxLength={2000}
                  rows={3}
                  invalid={Boolean(errors.emailBlurb)}
                  disabled={submitting}
                  /* Same rule as the subject above, and the same reason. */
                  onChange={(event) => {
                    blurbTouched.current = true;
                    patch({ emailBlurb: event.target.value });
                  }}
                />
              )}
            </Field>
          </div>

          {formMessages.length > 0 && (
            <Notice tone="danger" role="alert" title="Nothing was sent">
              {formMessages.map((line) => (
                <p key={line} className="m-0">
                  {line}
                </p>
              ))}
            </Notice>
          )}
        </form>
      </Modal>

      {/* Rendered AFTER the send dialog so the portal order stacks them above it. */}
      <QuotePdfPreviewModal
        target={previewTarget}
        onClose={() => setPreviewTarget(null)}
        hideSendForSignature
        hideOpenQuote
      />
      <Modal
        open={filePreviewUrl !== null}
        onClose={closeFilePreview}
        size="xl"
        title={`${PREVIEW_PDF_LABEL} · ${file?.name ?? quoteLabel}`}
        footer={
          <Button variant="secondary" onClick={closeFilePreview}>
            Close
          </Button>
        }
      >
        <div className="h-[70svh] min-w-0">
          {filePreviewUrl !== null && (
            /* No `sandbox`: it disables the browser's PDF viewer (see SendFromXeroButton). */
            <iframe
              src={filePreviewUrl}
              title={file?.name ?? `Quote ${quoteLabel}`}
              className="h-full w-full border-0"
            />
          )}
        </div>
      </Modal>

      {/*
        The last statement of the consequence, at the moment it becomes true.
        It names the record, the signer, the delivery fork and — where it
        applies — the fact that the signature carries no legal force, because
        this is the final screen on which any of that can still be reconsidered.
      */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void send()}
        loading={submitting}
        tone="danger"
        confirmLabel="Send for signature"
        cancelLabel="Go back"
        /*
         * One signer is named in the title, as it always was. Several are
         * COUNTED here and named in full below — four names in a dialog title
         * would truncate, and the one thing this screen must not do is imply the
         * list is shorter than it is.
         */
        title={
          multipleSigners
            ? `Send quote ${quoteLabel} to ${values.signers.length} signers?`
            : `Send quote ${quoteLabel} to ${firstSigner.name.trim() || 'the signer'}?`
        }
        description={
          <span className="grid gap-2">
            {/*
              WHICH document, named at the moment it becomes irreversible. On
              the fetch nobody has seen the file — the server gets it from Xero
              — so this is the only place that says so before it goes.
            */}
            {/*
              ⚠️ *SUPERSEDED 2026-08-31: wrapped in `path === 'document' && (…)`.*
              There is one path, so the sentence is unconditional. The SOURCE
              branch stays — it is still two real outcomes, and this is the one
              place that says which document is about to be signed.
            */}
            <span>
              {documentSource === 'xero'
                ? `Signs the PDF Xero holds${xeroRef.inXero && xeroRef.number ? ` (${xeroRef.number})` : ''}.`
                : `Signs ${file?.name ?? 'the file chosen above'}.`}
            </span>
            {/*
              🔴 **EVERY SIGNER AND EVERY ADDRESS, listed.** This is the last
              screen before a real signature request reaches real customers, and
              on `email` delivery each address below is written to the moment
              Confirm is pressed. A summary — "4 signers", the first name and
              "and 3 others" — would hide exactly the value somebody needs to
              re-read: a mistyped address on row 3 sends a customer's contract to
              a stranger, and nothing afterwards reports it.
            */}
            <span>
              {values.delivery === 'email'
                ? `DocuSign emails a request to sign ${quoteLabel} for ${total} to:`
                : `Creates an envelope covering ${quoteLabel} for ${total} for:`}
            </span>
            {/*
              `span`s, not a list: `ConfirmDialog` renders this description inside
              phrasing content, so a `ul` here is invalid nesting.
            */}
            <span className="grid gap-0.5">
              {values.signers.map((signer, index) => (
                <span key={index} className="[overflow-wrap:anywhere]">
                  {multipleSigners ? `Signer ${index + 1}: ` : ''}
                  {signerSummary(signer)}
                </span>
              ))}
            </span>
            <span>
              {values.delivery === 'email'
                ? 'No signing link afterwards.'
                : 'You deliver the link.'}
            </span>
            <span className="font-semibold text-warning">
              If signed, quote {quoteLabel} is accepted automatically.
            </span>
            {notBinding && (
              <span className="font-semibold text-danger">
                Not legally binding — DocuSign is on{' '}
                {docuSignEnvironmentLabel(connection?.environment)}.
              </span>
            )}
          </span>
        }
      />
    </>
  );
}
